import { defineMessages, type IntlShape, type MessageDescriptor } from "react-intl";

import { MetricTypes } from "../autogen/metrics";

/**
 * The display name of every metric.
 *
 * 58 of these are sentence-cased from the metric's own key, because no display copy was ever written for them and
 * the Django gettext catalog returns its input when a string is untranslated - so they were rendering as
 * `BLENDED_AD_SPEND` in titles and column headers. They read as placeholders; product copy should replace them.
 */
const metricDescriptions: Record<MetricTypes, MessageDescriptor> = defineMessages({
  [MetricTypes.PERCENT_OF_TOTAL_REVENUE]: {
    id: "Metric Name: PERCENT_OF_TOTAL_REVENUE",
    defaultMessage: "% of Total Revenue",
  },
  [MetricTypes.PERCENT_OF_TOTAL_SPEND]: {
    id: "Metric Name: PERCENT_OF_TOTAL_SPEND",
    defaultMessage: "% of Total Spend",
  },
  [MetricTypes.AOV]: { id: "Metric Name: AOV", defaultMessage: "Average Order Value" },
  [MetricTypes.ACTIVE_CAMPAIGNS]: { id: "Metric Name: ACTIVE_CAMPAIGNS", defaultMessage: "Active Campaigns" },
  [MetricTypes.AD_PLATFORM_REPORTED_REVENUE]: {
    id: "Metric Name: AD_PLATFORM_REPORTED_REVENUE",
    defaultMessage: "Ad Platform Reported Revenue",
  },
  [MetricTypes.ALL_TIME_REVENUE]: { id: "Metric Name: ALL_TIME_REVENUE", defaultMessage: "All Time Revenue" },
  [MetricTypes.ALL_TIME_SPEND]: { id: "Metric Name: ALL_TIME_SPEND", defaultMessage: "All Time Spend" },
  [MetricTypes.CAC]: { id: "Metric Name: CAC", defaultMessage: "Customer Acquisition Cost" },
  [MetricTypes.CUSTOMERS]: { id: "Metric Name: CUSTOMERS", defaultMessage: "Customers" },
  [MetricTypes.CUSTOMERS_EXISTING]: { id: "Metric Name: CUSTOMERS_EXISTING", defaultMessage: "Customers - Existing" },
  [MetricTypes.CUSTOMERS_NEW]: { id: "Metric Name: CUSTOMERS_NEW", defaultMessage: "Customers - New" },
  [MetricTypes.CUSTOMERS_NEW_PERCENTAGE]: {
    id: "Metric Name: CUSTOMERS_NEW_PERCENTAGE",
    defaultMessage: "Customers - New Percentage",
  },
  [MetricTypes.CUSTOMERS_TOTAL]: { id: "Metric Name: CUSTOMERS_TOTAL", defaultMessage: "Customers - Total" },
  [MetricTypes.DISCOUNTS]: { id: "Metric Name: DISCOUNTS", defaultMessage: "Discounts" },
  [MetricTypes.AD_ADDED_TO_CART]: { id: "Metric Name: AD_ADDED_TO_CART", defaultMessage: "Added To Cart" },
  [MetricTypes.AD_CLICKS]: { id: "Metric Name: AD_CLICKS", defaultMessage: "Clicks" },
  [MetricTypes.AD_FREQUENCY]: { id: "Metric Name: AD_FREQUENCY", defaultMessage: "Frequency" },
  [MetricTypes.AD_IMPRESSIONS]: { id: "Metric Name: AD_IMPRESSIONS", defaultMessage: "Impressions" },
  [MetricTypes.AD_REACH]: { id: "Metric Name: AD_REACH", defaultMessage: "Reach" },
  [MetricTypes.AD_SPEND]: { id: "Metric Name: AD_SPEND", defaultMessage: "Spend" },
  [MetricTypes.AD_VIEWS]: { id: "Metric Name: AD_VIEWS", defaultMessage: "Views" },
  [MetricTypes.AD_CTR]: { id: "Metric Name: AD_CTR", defaultMessage: "Click-Through ratio" },
  [MetricTypes.AD_AOV]: { id: "Metric Name: AD_AOV", defaultMessage: "Average Order Value" },
  [MetricTypes.AD_CONVERSION_RATE]: { id: "Metric Name: AD_CONVERSION_RATE", defaultMessage: "Conversion rate" },
  [MetricTypes.AD_COST_PER_CONVERSION]: {
    id: "Metric Name: AD_COST_PER_CONVERSION",
    defaultMessage: "Cost Per Conversion",
  },
  [MetricTypes.AD_COST_PER_IMPRESSION]: {
    id: "Metric Name: AD_COST_PER_IMPRESSION",
    defaultMessage: "Ad cost per impression",
  },
  [MetricTypes.AD_COST_PER_CLICK]: { id: "Metric Name: AD_COST_PER_CLICK", defaultMessage: "Ad cost per click" },
  [MetricTypes.AD_ATTRIBUTED_CONVERSION_ROAS]: {
    id: "Metric Name: AD_ATTRIBUTED_CONVERSION_ROAS",
    defaultMessage: "ROAS",
  },
  [MetricTypes.AD_ATTRIBUTED_CONVERSION_VALUE]: {
    id: "Metric Name: AD_ATTRIBUTED_CONVERSION_VALUE",
    defaultMessage: "Conversion Value",
  },
  [MetricTypes.AD_ATTRIBUTED_CONVERSION_COUNT]: {
    id: "Metric Name: AD_ATTRIBUTED_CONVERSION_COUNT",
    defaultMessage: "Conversion count",
  },
  [MetricTypes.MARKETING_EFFICIENCY_RATIO]: {
    id: "Metric Name: MARKETING_EFFICIENCY_RATIO",
    defaultMessage: "Marketing efficiency ratio",
  },
  [MetricTypes.ACQUISITION_MARKETING_EFFICIENCY_RATIO]: {
    id: "Metric Name: ACQUISITION_MARKETING_EFFICIENCY_RATIO",
    defaultMessage: "Acquisition marketing efficiency ratio",
  },
  [MetricTypes.BLENDED_AD_CLICKS]: { id: "Metric Name: BLENDED_AD_CLICKS", defaultMessage: "Blended ad clicks" },
  [MetricTypes.BLENDED_AD_FREQUENCY]: {
    id: "Metric Name: BLENDED_AD_FREQUENCY",
    defaultMessage: "Blended ad frequency",
  },
  [MetricTypes.BLENDED_AD_IMPRESSIONS]: {
    id: "Metric Name: BLENDED_AD_IMPRESSIONS",
    defaultMessage: "Blended ad impressions",
  },
  [MetricTypes.BLENDED_AD_REACH]: { id: "Metric Name: BLENDED_AD_REACH", defaultMessage: "Blended ad reach" },
  [MetricTypes.BLENDED_AD_SPEND]: { id: "Metric Name: BLENDED_AD_SPEND", defaultMessage: "Blended ad spend" },
  [MetricTypes.BLENDED_AD_VIEWS]: { id: "Metric Name: BLENDED_AD_VIEWS", defaultMessage: "Blended ad views" },
  [MetricTypes.BLENDED_AD_CTR]: { id: "Metric Name: BLENDED_AD_CTR", defaultMessage: "Blended ad CTR" },
  [MetricTypes.BLENDED_AD_AOV]: { id: "Metric Name: BLENDED_AD_AOV", defaultMessage: "Blended ad AOV" },
  [MetricTypes.BLENDED_AD_CONVERSION_RATE]: {
    id: "Metric Name: BLENDED_AD_CONVERSION_RATE",
    defaultMessage: "Blended ad conversion rate",
  },
  [MetricTypes.BLENDED_AD_COST_PER_CONVERSION]: {
    id: "Metric Name: BLENDED_AD_COST_PER_CONVERSION",
    defaultMessage: "Blended ad cost per conversion",
  },
  [MetricTypes.BLENDED_AD_COST_PER_IMPRESSION]: {
    id: "Metric Name: BLENDED_AD_COST_PER_IMPRESSION",
    defaultMessage: "Blended ad cost per impression",
  },
  [MetricTypes.BLENDED_AD_COST_PER_CLICK]: {
    id: "Metric Name: BLENDED_AD_COST_PER_CLICK",
    defaultMessage: "Blended ad cost per click",
  },
  [MetricTypes.BLENDED_AD_ATTRIBUTED_CONVERSION_COUNT]: {
    id: "Metric Name: BLENDED_AD_ATTRIBUTED_CONVERSION_COUNT",
    defaultMessage: "Blended ad attributed conversion count",
  },
  [MetricTypes.BLENDED_AD_ATTRIBUTED_CONVERSION_VALUE]: {
    id: "Metric Name: BLENDED_AD_ATTRIBUTED_CONVERSION_VALUE",
    defaultMessage: "Blended ad attributed conversion value",
  },
  [MetricTypes.BLENDED_AD_ATTRIBUTED_CONVERSION_ROAS]: {
    id: "Metric Name: BLENDED_AD_ATTRIBUTED_CONVERSION_ROAS",
    defaultMessage: "Blended ad attributed conversion ROAS",
  },
  [MetricTypes.BLENDED_AD_RELATIVE_PERCENTAGE_SPEND]: {
    id: "Metric Name: BLENDED_AD_RELATIVE_PERCENTAGE_SPEND",
    defaultMessage: "Blended ad relative percentage spend",
  },
  [MetricTypes.META_AD_ADDED_TO_CART]: { id: "Metric Name: META_AD_ADDED_TO_CART", defaultMessage: "Added To Cart" },
  [MetricTypes.META_AD_CLICKS]: { id: "Metric Name: META_AD_CLICKS", defaultMessage: "Clicks" },
  [MetricTypes.META_AD_FREQUENCY]: { id: "Metric Name: META_AD_FREQUENCY", defaultMessage: "Frequency" },
  [MetricTypes.META_AD_IMPRESSIONS]: { id: "Metric Name: META_AD_IMPRESSIONS", defaultMessage: "Impressions" },
  [MetricTypes.META_AD_REACH]: { id: "Metric Name: META_AD_REACH", defaultMessage: "Reach" },
  [MetricTypes.META_AD_SPEND]: { id: "Metric Name: META_AD_SPEND", defaultMessage: "Spend" },
  [MetricTypes.META_AD_VIEWS]: { id: "Metric Name: META_AD_VIEWS", defaultMessage: "Views" },
  [MetricTypes.META_AD_CTR]: { id: "Metric Name: META_AD_CTR", defaultMessage: "Click-Through-Rate" },
  [MetricTypes.META_AD_AOV]: { id: "Metric Name: META_AD_AOV", defaultMessage: "Average Order Value" },
  [MetricTypes.META_AD_CONVERSION_RATE]: {
    id: "Metric Name: META_AD_CONVERSION_RATE",
    defaultMessage: "Conversion Rate",
  },
  [MetricTypes.META_AD_COST_PER_CONVERSION]: {
    id: "Metric Name: META_AD_COST_PER_CONVERSION",
    defaultMessage: "Cost Per Conversion",
  },
  [MetricTypes.META_AD_COST_PER_IMPRESSION]: {
    id: "Metric Name: META_AD_COST_PER_IMPRESSION",
    defaultMessage: "Meta ad cost per impression",
  },
  [MetricTypes.META_AD_COST_PER_MILLE]: { id: "Metric Name: META_AD_COST_PER_MILLE", defaultMessage: "Cost Per Mille" },
  [MetricTypes.META_AD_COST_PER_CLICK]: { id: "Metric Name: META_AD_COST_PER_CLICK", defaultMessage: "Cost Per Click" },
  [MetricTypes.META_AD_ATTRIBUTED_CONVERSION_COUNT]: {
    id: "Metric Name: META_AD_ATTRIBUTED_CONVERSION_COUNT",
    defaultMessage: "Conversion Count",
  },
  [MetricTypes.META_AD_ATTRIBUTED_CONVERSION_VALUE]: {
    id: "Metric Name: META_AD_ATTRIBUTED_CONVERSION_VALUE",
    defaultMessage: "Conversion Value",
  },
  [MetricTypes.META_AD_ATTRIBUTED_CONVERSION_ROAS]: {
    id: "Metric Name: META_AD_ATTRIBUTED_CONVERSION_ROAS",
    defaultMessage: "Attributed ROAS",
  },
  [MetricTypes.META_AD_RELATIVE_PERCENTAGE_SPEND]: {
    id: "Metric Name: META_AD_RELATIVE_PERCENTAGE_SPEND",
    defaultMessage: "Meta ad relative percentage spend",
  },
  [MetricTypes.GOOGLE_AD_CLICKS]: { id: "Metric Name: GOOGLE_AD_CLICKS", defaultMessage: "Clicks" },
  [MetricTypes.GOOGLE_AD_FREQUENCY]: { id: "Metric Name: GOOGLE_AD_FREQUENCY", defaultMessage: "Frequency" },
  [MetricTypes.GOOGLE_AD_IMPRESSIONS]: { id: "Metric Name: GOOGLE_AD_IMPRESSIONS", defaultMessage: "Impressions" },
  [MetricTypes.GOOGLE_AD_REACH]: { id: "Metric Name: GOOGLE_AD_REACH", defaultMessage: "Reach" },
  [MetricTypes.GOOGLE_AD_SPEND]: { id: "Metric Name: GOOGLE_AD_SPEND", defaultMessage: "Google Ads Spend" },
  [MetricTypes.GOOGLE_AD_VIEWS]: { id: "Metric Name: GOOGLE_AD_VIEWS", defaultMessage: "Views" },
  [MetricTypes.GOOGLE_AD_CTR]: { id: "Metric Name: GOOGLE_AD_CTR", defaultMessage: "Click-Through Ratio" },
  [MetricTypes.GOOGLE_AD_AOV]: { id: "Metric Name: GOOGLE_AD_AOV", defaultMessage: "Average Order Value" },
  [MetricTypes.GOOGLE_AD_CONVERSION_RATE]: {
    id: "Metric Name: GOOGLE_AD_CONVERSION_RATE",
    defaultMessage: "Attributed Conversion Count",
  },
  [MetricTypes.GOOGLE_AD_COST_PER_CONVERSION]: {
    id: "Metric Name: GOOGLE_AD_COST_PER_CONVERSION",
    defaultMessage: "Cost Per Conversion",
  },
  [MetricTypes.GOOGLE_AD_COST_PER_IMPRESSION]: {
    id: "Metric Name: GOOGLE_AD_COST_PER_IMPRESSION",
    defaultMessage: "Google ad cost per impression",
  },
  [MetricTypes.GOOGLE_AD_COST_PER_CLICK]: {
    id: "Metric Name: GOOGLE_AD_COST_PER_CLICK",
    defaultMessage: "Google ad cost per click",
  },
  [MetricTypes.GOOGLE_AD_ATTRIBUTED_CONVERSION_COUNT]: {
    id: "Metric Name: GOOGLE_AD_ATTRIBUTED_CONVERSION_COUNT",
    defaultMessage: "Conversion Count",
  },
  [MetricTypes.GOOGLE_AD_ATTRIBUTED_CONVERSION_VALUE]: {
    id: "Metric Name: GOOGLE_AD_ATTRIBUTED_CONVERSION_VALUE",
    defaultMessage: "Conversion Value",
  },
  [MetricTypes.GOOGLE_AD_ATTRIBUTED_CONVERSION_ROAS]: {
    id: "Metric Name: GOOGLE_AD_ATTRIBUTED_CONVERSION_ROAS",
    defaultMessage: "Google Ads ROAS",
  },
  [MetricTypes.GOOGLE_AD_RELATIVE_PERCENTAGE_SPEND]: {
    id: "Metric Name: GOOGLE_AD_RELATIVE_PERCENTAGE_SPEND",
    defaultMessage: "Google ad relative percentage spend",
  },
  [MetricTypes.TIKTOK_AD_SPEND]: { id: "Metric Name: TIKTOK_AD_SPEND", defaultMessage: "TikTok Ad Spend" },
  [MetricTypes.TIKTOK_ACCOUNT_ID]: { id: "Metric Name: TIKTOK_ACCOUNT_ID", defaultMessage: "TikTok Account ID" },
  [MetricTypes.TIKTOK_ACCOUNT_NAME]: { id: "Metric Name: TIKTOK_ACCOUNT_NAME", defaultMessage: "TikTok Account Name" },
  [MetricTypes.TIKTOK_WAYFLYER_BC_ID]: {
    id: "Metric Name: TIKTOK_WAYFLYER_BC_ID",
    defaultMessage: "Wayflyer Business Center ID",
  },
  [MetricTypes.GROSS_ITEM_QUANTITY]: {
    id: "Metric Name: GROSS_ITEM_QUANTITY",
    defaultMessage: "Total Quantity Inc. Returns",
  },
  [MetricTypes.GROSS_ITEM_QUANTITY_LESS_REFUNDED_QUANTITY_FROM_PRODUCTS]: {
    id: "Metric Name: GROSS_ITEM_QUANTITY_LESS_REFUNDED_QUANTITY_FROM_PRODUCTS",
    defaultMessage: "Total Quantity",
  },
  [MetricTypes.GROSS_ITEM_QUANTITY_LESS_REFUNDED_QUANTITY_FROM_REFUNDS]: {
    id: "Metric Name: GROSS_ITEM_QUANTITY_LESS_REFUNDED_QUANTITY_FROM_REFUNDS",
    defaultMessage: "Total Quantity",
  },
  [MetricTypes.GROSS_PRODUCT_REVENUE_FROM_ORDER_PROPORTION_LESS_DISCOUNTS]: {
    id: "Metric Name: GROSS_PRODUCT_REVENUE_FROM_ORDER_PROPORTION_LESS_DISCOUNTS",
    defaultMessage: "Total Sales",
  },
  [MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_LESS_REFUNDS_FROM_ORDERS_PLUS_SHIPPING_PLUS_TAX]: {
    id: "Metric Name: GROSS_REVENUE_LESS_DISCOUNTS_LESS_REFUNDS_FROM_ORDERS_PLUS_SHIPPING_PLUS_TAX",
    defaultMessage: "Total Sales",
  },
  [MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_LESS_REFUNDS_PLUS_SHIPPING_PLUS_TAX]: {
    id: "Metric Name: GROSS_REVENUE_LESS_DISCOUNTS_LESS_REFUNDS_PLUS_SHIPPING_PLUS_TAX",
    defaultMessage: "Gross revenue less discounts less refunds plus shipping plus tax",
  },
  [MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_PLUS_SHIPPING_PLUS_TAX]: {
    id: "Metric Name: GROSS_REVENUE_LESS_DISCOUNTS_PLUS_SHIPPING_PLUS_TAX",
    defaultMessage: "Gross Revenue",
  },
  [MetricTypes.ACQUISITION_GROSS_REVENUE_LESS_DISCOUNTS_PLUS_SHIPPING_PLUS_TAX]: {
    id: "Metric Name: ACQUISITION_GROSS_REVENUE_LESS_DISCOUNTS_PLUS_SHIPPING_PLUS_TAX",
    defaultMessage: "Acquisition gross revenue less discounts plus shipping plus tax",
  },
  [MetricTypes.GROSS_REVENUE]: { id: "Metric Name: GROSS_REVENUE", defaultMessage: "Gross Revenue" },
  [MetricTypes.NEW_CUSTOMER_ITEM_QUANTITY]: {
    id: "Metric Name: NEW_CUSTOMER_ITEM_QUANTITY",
    defaultMessage: "New customer item quantity",
  },
  [MetricTypes.NEW_CUSTOMER_ITEM_QUANTITY_LESS_REFUNDED_QUANTITY_FROM_PRODUCTS]: {
    id: "Metric Name: NEW_CUSTOMER_ITEM_QUANTITY_LESS_REFUNDED_QUANTITY_FROM_PRODUCTS",
    defaultMessage: "New customer item quantity less refunded quantity from products",
  },
  [MetricTypes.ITEM_LEVEL_NEW_CUSTOMER_PERCENT]: {
    id: "Metric Name: ITEM_LEVEL_NEW_CUSTOMER_PERCENT",
    defaultMessage: "New Customer Rate",
  },
  [MetricTypes.ITEM_LEVEL_NEW_CUSTOMER_PERCENT_FROM_PRODUCTS]: {
    id: "Metric Name: ITEM_LEVEL_NEW_CUSTOMER_PERCENT_FROM_PRODUCTS",
    defaultMessage: "New Customer Rate",
  },
  [MetricTypes.ITEM_LEVEL_RETURN_RATE_FROM_PRODUCTS]: {
    id: "Metric Name: ITEM_LEVEL_RETURN_RATE_FROM_PRODUCTS",
    defaultMessage: "Return Rate %",
  },
  [MetricTypes.ITEM_LEVEL_RETURN_RATE_FROM_REFUNDS]: {
    id: "Metric Name: ITEM_LEVEL_RETURN_RATE_FROM_REFUNDS",
    defaultMessage: "Return Rate %",
  },
  [MetricTypes.RETURNING_CUSTOMER_PERCENTAGE]: {
    id: "Metric Name: RETURNING_CUSTOMER_PERCENTAGE",
    defaultMessage: "Returning Customer Rate",
  },
  [MetricTypes.LTV]: { id: "Metric Name: LTV", defaultMessage: "LTV" },
  [MetricTypes.LINK_CLICKS]: { id: "Metric Name: LINK_CLICKS", defaultMessage: "Link Clicks" },
  [MetricTypes.MARKETING_SPEND]: { id: "Metric Name: MARKETING_SPEND", defaultMessage: "Total Marketing Spend" },
  [MetricTypes.LTV_CAC_MONTH]: { id: "Metric Name: LTV_CAC_MONTH", defaultMessage: "Month" },
  [MetricTypes.MONTH_FIRST_ORDER]: { id: "Metric Name: MONTH_FIRST_ORDER", defaultMessage: "Month First Order" },
  [MetricTypes.NET_QUANTITY]: { id: "Metric Name: NET_QUANTITY", defaultMessage: "Net Quantity" },
  [MetricTypes.NET_REVENUE]: { id: "Metric Name: NET_REVENUE", defaultMessage: "Net Revenue" },
  [MetricTypes.NET_SALES_EXISTING]: { id: "Metric Name: NET_SALES_EXISTING", defaultMessage: "Net Sales - Existing" },
  [MetricTypes.NET_SALES_NEW]: { id: "Metric Name: NET_SALES_NEW", defaultMessage: "Net Sales - New" },
  [MetricTypes.NET_SALES_NEW_PERCENTAGE]: {
    id: "Metric Name: NET_SALES_NEW_PERCENTAGE",
    defaultMessage: "Net Sales - New Percentage",
  },
  [MetricTypes.NET_SALES_TOTAL]: { id: "Metric Name: NET_SALES_TOTAL", defaultMessage: "Net Sales - Total" },
  [MetricTypes.NEW_CUSTOMERS]: { id: "Metric Name: NEW_CUSTOMERS", defaultMessage: "New Customers" },
  [MetricTypes.ORDERS]: { id: "Metric Name: ORDERS", defaultMessage: "Orders" },
  [MetricTypes.ORDERS_EXISTING]: { id: "Metric Name: ORDERS_EXISTING", defaultMessage: "Orders - Existing" },
  [MetricTypes.ORDERS_NEW]: { id: "Metric Name: ORDERS_NEW", defaultMessage: "Orders - New" },
  [MetricTypes.ORDERS_NEW_PERCENTAGE]: {
    id: "Metric Name: ORDERS_NEW_PERCENTAGE",
    defaultMessage: "Orders - New Percentage",
  },
  [MetricTypes.ORDERS_TOTAL]: { id: "Metric Name: ORDERS_TOTAL", defaultMessage: "Orders - Total" },
  [MetricTypes.PRODUCT_QUANTITY]: { id: "Metric Name: PRODUCT_QUANTITY", defaultMessage: "Product quantity" },
  [MetricTypes.PRODUCT_REFUNDS_FROM_PRODUCTS]: {
    id: "Metric Name: PRODUCT_REFUNDS_FROM_PRODUCTS",
    defaultMessage: "Total Returns",
  },
  [MetricTypes.PRODUCT_REFUNDS_FROM_REFUNDS]: {
    id: "Metric Name: PRODUCT_REFUNDS_FROM_REFUNDS",
    defaultMessage: "Total Returns",
  },
  [MetricTypes.QUANTITY]: { id: "Metric Name: QUANTITY", defaultMessage: "Quantity" },
  [MetricTypes.LTV_CAC_QUARTER]: { id: "Metric Name: LTV_CAC_QUARTER", defaultMessage: "Quarter" },
  [MetricTypes.QUARTER_FIRST_ORDER]: { id: "Metric Name: QUARTER_FIRST_ORDER", defaultMessage: "Quarter First Order" },
  [MetricTypes.ROAS]: { id: "Metric Name: ROAS", defaultMessage: "Marketing Efficiency Ratio" },
  [MetricTypes.REFUNDS_PERCENT]: { id: "Metric Name: REFUNDS_PERCENT", defaultMessage: "Refunds %" },
  [MetricTypes.RETURN_RATE]: { id: "Metric Name: RETURN_RATE", defaultMessage: "Return Rate" },
  [MetricTypes.RETURNS]: { id: "Metric Name: RETURNS", defaultMessage: "Returns" },
  [MetricTypes.REVENUE]: { id: "Metric Name: REVENUE", defaultMessage: "Revenue" },
  [MetricTypes.SALES]: { id: "Metric Name: SALES", defaultMessage: "Sales" },
  [MetricTypes.SALES_EXISTING]: { id: "Metric Name: SALES_EXISTING", defaultMessage: "Sales - Existing" },
  [MetricTypes.SALES_NEW]: { id: "Metric Name: SALES_NEW", defaultMessage: "Sales - New" },
  [MetricTypes.SALES_NEW_PERCENTAGE]: {
    id: "Metric Name: SALES_NEW_PERCENTAGE",
    defaultMessage: "Sales - New Percentage",
  },
  [MetricTypes.SALES_TOTAL]: { id: "Metric Name: SALES_TOTAL", defaultMessage: "Sales - Total" },
  [MetricTypes.SALES_EXCLUDING_REFUNDS]: {
    id: "Metric Name: SALES_EXCLUDING_REFUNDS",
    defaultMessage: "Sales excluding Refunds",
  },
  [MetricTypes.TOTAL_ORDERED_ITEM_QUANTITY]: {
    id: "Metric Name: TOTAL_ORDERED_ITEM_QUANTITY",
    defaultMessage: "Total Units Sold",
  },
  [MetricTypes.TOTAL_ORDERED_ITEM_QUANTITY_LESS_REFUNDED_ITEM_QUANTITY]: {
    id: "Metric Name: TOTAL_ORDERED_ITEM_QUANTITY_LESS_REFUNDED_ITEM_QUANTITY",
    defaultMessage: "Net Item Quantity",
  },
  [MetricTypes.TOTAL_ORDERED_ITEM_QUANTITY_LESS_REFUNDED_ITEM_QUANTITY_BY_REFUNDED_DATE]: {
    id: "Metric Name: TOTAL_ORDERED_ITEM_QUANTITY_LESS_REFUNDED_ITEM_QUANTITY_BY_REFUNDED_DATE",
    defaultMessage: "Net Item Quantity",
  },
  [MetricTypes.TOTAL_ORDER_QUANTITY]: {
    id: "Metric Name: TOTAL_ORDER_QUANTITY",
    defaultMessage: "Total Order Quantity",
  },
  [MetricTypes.TOTAL_REFUNDED_ITEM_QUANTITY]: {
    id: "Metric Name: TOTAL_REFUNDED_ITEM_QUANTITY",
    defaultMessage: "Total refunded item quantity",
  },
  [MetricTypes.TOTAL_REFUNDED_ITEM_QUANTITY_BY_REFUNDED_DATE]: {
    id: "Metric Name: TOTAL_REFUNDED_ITEM_QUANTITY_BY_REFUNDED_DATE",
    defaultMessage: "Total refunded item quantity by refunded date",
  },
  [MetricTypes.TOTAL_UNIQUE_CUSTOMER_COUNT]: {
    id: "Metric Name: TOTAL_UNIQUE_CUSTOMER_COUNT",
    defaultMessage: "Total unique customer count",
  },
  [MetricTypes.TOTAL_UNIQUE_NEW_CUSTOMER_COUNT]: {
    id: "Metric Name: TOTAL_UNIQUE_NEW_CUSTOMER_COUNT",
    defaultMessage: "New Customers",
  },
  [MetricTypes.TOTAL_SALES]: { id: "Metric Name: TOTAL_SALES", defaultMessage: "Total Sales" },
  [MetricTypes.TRANSACTIONS]: { id: "Metric Name: TRANSACTIONS", defaultMessage: "Transactions" },
  [MetricTypes.LTV_CAC_YEAR]: { id: "Metric Name: LTV_CAC_YEAR", defaultMessage: "Year" },
  [MetricTypes.YEAR_FIRST_ORDER]: { id: "Metric Name: YEAR_FIRST_ORDER", defaultMessage: "Year First Order" },
  [MetricTypes.ADDITIONAL_COGS_AND_SHIPPING]: {
    id: "Metric Name: ADDITIONAL_COGS_AND_SHIPPING",
    defaultMessage: "CoGS and Shipping",
  },
  [MetricTypes.ADDITIONAL_OPEX]: { id: "Metric Name: ADDITIONAL_OPEX", defaultMessage: "Monthly OPEX" },
  [MetricTypes.COGS]: { id: "Metric Name: COGS", defaultMessage: "Cost of Goods Sold" },
  [MetricTypes.GROSS_PROFIT]: { id: "Metric Name: GROSS_PROFIT", defaultMessage: "Gross Profit" },
  [MetricTypes.GROSS_MARGIN_PERCENTAGE]: {
    id: "Metric Name: GROSS_MARGIN_PERCENTAGE",
    defaultMessage: "Gross Margin %",
  },
  [MetricTypes.FCP]: { id: "Metric Name: FCP", defaultMessage: "First Order at" },
  [MetricTypes.TEST_METRIC]: { id: "Metric Name: TEST_METRIC", defaultMessage: "Test metric" },
  [MetricTypes.PROFIT]: { id: "Metric Name: PROFIT", defaultMessage: "Net Profit" },
  [MetricTypes.PERCENTAGE_OF_SPEND]: { id: "Metric Name: PERCENTAGE_OF_SPEND", defaultMessage: "% of Spend" },
  [MetricTypes.PERCENTAGE_OF_TOTAL_AD_SPEND]: {
    id: "Metric Name: PERCENTAGE_OF_TOTAL_AD_SPEND",
    defaultMessage: "% of spend",
  },
  [MetricTypes.AVERAGE_UNITS_PER_ORDER]: {
    id: "Metric Name: AVERAGE_UNITS_PER_ORDER",
    defaultMessage: "Average Units/Order",
  },
  [MetricTypes.REFUNDED_ITEM_PERCENTAGE]: {
    id: "Metric Name: REFUNDED_ITEM_PERCENTAGE",
    defaultMessage: "Return Rate %",
  },
  [MetricTypes.REFUNDED_ITEM_PERCENTAGE_BY_REFUNDED_DATE]: {
    id: "Metric Name: REFUNDED_ITEM_PERCENTAGE_BY_REFUNDED_DATE",
    defaultMessage: "Return Rate %",
  },
  [MetricTypes.AVERAGE_DAILY_UNITS]: { id: "Metric Name: AVERAGE_DAILY_UNITS", defaultMessage: "Average Daily Units" },
  [MetricTypes.SALES_VELOCITY_THIRTY_DAYS]: {
    id: "Metric Name: SALES_VELOCITY_THIRTY_DAYS",
    defaultMessage: "Sales Velocity (30 days)",
  },
  [MetricTypes.GROSS_PRODUCT_REVENUE]: {
    id: "Metric Name: GROSS_PRODUCT_REVENUE",
    defaultMessage: "Gross product revenue",
  },
  [MetricTypes.PRODUCT_LEVEL_DISCOUNT_PCT]: {
    id: "Metric Name: PRODUCT_LEVEL_DISCOUNT_PCT",
    defaultMessage: "Discount %",
  },
  [MetricTypes.PRODUCT_LEVEL_DISCOUNT_VALUE]: {
    id: "Metric Name: PRODUCT_LEVEL_DISCOUNT_VALUE",
    defaultMessage: "Product level discount value",
  },
  [MetricTypes.SESSION_COUNT]: { id: "Metric Name: SESSION_COUNT", defaultMessage: "Store Session Count" },
  [MetricTypes.CONVERSION_RATE]: { id: "Metric Name: CONVERSION_RATE", defaultMessage: "Store Conversion Rate" },
  [MetricTypes.STORE_NAME]: { id: "Metric Name: STORE_NAME", defaultMessage: "Store" },
  [MetricTypes.ALLTIME_AOV]: { id: "Metric Name: ALLTIME_AOV", defaultMessage: "Alltime AOV" },
  [MetricTypes.ALLTIME_FREQUENCY]: { id: "Metric Name: ALLTIME_FREQUENCY", defaultMessage: "Alltime Frequency" },
  [MetricTypes.ALLTIME_LTV]: { id: "Metric Name: ALLTIME_LTV", defaultMessage: "Alltime LTV" },
  [MetricTypes.ALLTIME_NET_REVENUE]: { id: "Metric Name: ALLTIME_NET_REVENUE", defaultMessage: "Alltime Net Revenue" },
  [MetricTypes.ALLTIME_UNIQUE_CUSTOMER_COUNT]: {
    id: "Metric Name: ALLTIME_UNIQUE_CUSTOMER_COUNT",
    defaultMessage: "Alltime Unique Customer Count",
  },
  [MetricTypes.ALLTIME_ORDERS]: { id: "Metric Name: ALLTIME_ORDERS", defaultMessage: "Alltime Orders" },
  [MetricTypes.ALLTIME_CUSTOMER_CURRENCY_CPC_SPEND]: {
    id: "Metric Name: ALLTIME_CUSTOMER_CURRENCY_CPC_SPEND",
    defaultMessage: "Alltime CPC Spend",
  },
  [MetricTypes.ALLTIME_CAC]: { id: "Metric Name: ALLTIME_CAC", defaultMessage: "Alltime CAC" },
  [MetricTypes.SAME_MONTH_AOV]: { id: "Metric Name: SAME_MONTH_AOV", defaultMessage: "Same Month AOV" },
  [MetricTypes.SAME_MONTH_FREQUENCY]: {
    id: "Metric Name: SAME_MONTH_FREQUENCY",
    defaultMessage: "Same Month Frequency",
  },
  [MetricTypes.SAME_MONTH_LTV]: { id: "Metric Name: SAME_MONTH_LTV", defaultMessage: "Same Month LTV" },
  [MetricTypes.SAME_MONTH_NET_REVENUE]: {
    id: "Metric Name: SAME_MONTH_NET_REVENUE",
    defaultMessage: "Same Month Net Revenue",
  },
  [MetricTypes.SAME_MONTH_UNIQUE_CUSTOMER_COUNT]: {
    id: "Metric Name: SAME_MONTH_UNIQUE_CUSTOMER_COUNT",
    defaultMessage: "Same Month Unique Customer Count",
  },
  [MetricTypes.SAME_MONTH_ORDERS]: { id: "Metric Name: SAME_MONTH_ORDERS", defaultMessage: "Same Month Orders" },
  [MetricTypes.SAME_MONTH_CUSTOMER_CURRENCY_CPC_SPEND]: {
    id: "Metric Name: SAME_MONTH_CUSTOMER_CURRENCY_CPC_SPEND",
    defaultMessage: "Same Month CPC Spend",
  },
  [MetricTypes.SAME_MONTH_CAC]: { id: "Metric Name: SAME_MONTH_CAC", defaultMessage: "Same Month CAC" },
  [MetricTypes.ONE_MONTH_AOV]: { id: "Metric Name: ONE_MONTH_AOV", defaultMessage: "One Month AOV" },
  [MetricTypes.ONE_MONTH_FREQUENCY]: { id: "Metric Name: ONE_MONTH_FREQUENCY", defaultMessage: "One Month Frequency" },
  [MetricTypes.ONE_MONTH_LTV]: { id: "Metric Name: ONE_MONTH_LTV", defaultMessage: "One Month LTV" },
  [MetricTypes.ONE_MONTH_NET_REVENUE]: {
    id: "Metric Name: ONE_MONTH_NET_REVENUE",
    defaultMessage: "One Month Net Revenue",
  },
  [MetricTypes.ONE_MONTH_UNIQUE_CUSTOMER_COUNT]: {
    id: "Metric Name: ONE_MONTH_UNIQUE_CUSTOMER_COUNT",
    defaultMessage: "One Month Unique Customer Count",
  },
  [MetricTypes.ONE_MONTH_ORDERS]: { id: "Metric Name: ONE_MONTH_ORDERS", defaultMessage: "One Month Orders" },
  [MetricTypes.ONE_MONTH_CUSTOMER_CURRENCY_CPC_SPEND]: {
    id: "Metric Name: ONE_MONTH_CUSTOMER_CURRENCY_CPC_SPEND",
    defaultMessage: "One Month CPC Spend",
  },
  [MetricTypes.ONE_MONTH_CAC]: { id: "Metric Name: ONE_MONTH_CAC", defaultMessage: "One Month CAC" },
  [MetricTypes.TWO_MONTH_AOV]: { id: "Metric Name: TWO_MONTH_AOV", defaultMessage: "Two Month AOV" },
  [MetricTypes.TWO_MONTH_FREQUENCY]: { id: "Metric Name: TWO_MONTH_FREQUENCY", defaultMessage: "Two Month Frequency" },
  [MetricTypes.TWO_MONTH_LTV]: { id: "Metric Name: TWO_MONTH_LTV", defaultMessage: "Two Month LTV" },
  [MetricTypes.TWO_MONTH_NET_REVENUE]: {
    id: "Metric Name: TWO_MONTH_NET_REVENUE",
    defaultMessage: "Two Month Net Revenue",
  },
  [MetricTypes.TWO_MONTH_UNIQUE_CUSTOMER_COUNT]: {
    id: "Metric Name: TWO_MONTH_UNIQUE_CUSTOMER_COUNT",
    defaultMessage: "Two Month Unique Customer Count",
  },
  [MetricTypes.TWO_MONTH_ORDERS]: { id: "Metric Name: TWO_MONTH_ORDERS", defaultMessage: "Two Month Orders" },
  [MetricTypes.TWO_MONTH_CUSTOMER_CURRENCY_CPC_SPEND]: {
    id: "Metric Name: TWO_MONTH_CUSTOMER_CURRENCY_CPC_SPEND",
    defaultMessage: "Two Month CPC Spend",
  },
  [MetricTypes.TWO_MONTH_CAC]: { id: "Metric Name: TWO_MONTH_CAC", defaultMessage: "Two Month CAC" },
  [MetricTypes.THREE_MONTH_AOV]: { id: "Metric Name: THREE_MONTH_AOV", defaultMessage: "Three Month AOV" },
  [MetricTypes.THREE_MONTH_FREQUENCY]: {
    id: "Metric Name: THREE_MONTH_FREQUENCY",
    defaultMessage: "Three Month Frequency",
  },
  [MetricTypes.THREE_MONTH_LTV]: { id: "Metric Name: THREE_MONTH_LTV", defaultMessage: "Three Month LTV" },
  [MetricTypes.THREE_MONTH_NET_REVENUE]: {
    id: "Metric Name: THREE_MONTH_NET_REVENUE",
    defaultMessage: "Three Month Net Revenue",
  },
  [MetricTypes.THREE_MONTH_UNIQUE_CUSTOMER_COUNT]: {
    id: "Metric Name: THREE_MONTH_UNIQUE_CUSTOMER_COUNT",
    defaultMessage: "Three Month Unique Customer Count",
  },
  [MetricTypes.THREE_MONTH_ORDERS]: { id: "Metric Name: THREE_MONTH_ORDERS", defaultMessage: "Three Month Orders" },
  [MetricTypes.THREE_MONTH_CUSTOMER_CURRENCY_CPC_SPEND]: {
    id: "Metric Name: THREE_MONTH_CUSTOMER_CURRENCY_CPC_SPEND",
    defaultMessage: "Three Month CPC Spend",
  },
  [MetricTypes.THREE_MONTH_CAC]: { id: "Metric Name: THREE_MONTH_CAC", defaultMessage: "Three Month CAC" },
  [MetricTypes.FOUR_MONTH_AOV]: { id: "Metric Name: FOUR_MONTH_AOV", defaultMessage: "Four Month AOV" },
  [MetricTypes.FOUR_MONTH_FREQUENCY]: {
    id: "Metric Name: FOUR_MONTH_FREQUENCY",
    defaultMessage: "Four Month Frequency",
  },
  [MetricTypes.FOUR_MONTH_LTV]: { id: "Metric Name: FOUR_MONTH_LTV", defaultMessage: "Four Month LTV" },
  [MetricTypes.FOUR_MONTH_NET_REVENUE]: {
    id: "Metric Name: FOUR_MONTH_NET_REVENUE",
    defaultMessage: "Four Month Net Revenue",
  },
  [MetricTypes.FOUR_MONTH_UNIQUE_CUSTOMER_COUNT]: {
    id: "Metric Name: FOUR_MONTH_UNIQUE_CUSTOMER_COUNT",
    defaultMessage: "Four Month Unique Customer Count",
  },
  [MetricTypes.FOUR_MONTH_ORDERS]: { id: "Metric Name: FOUR_MONTH_ORDERS", defaultMessage: "Four Month Orders" },
  [MetricTypes.FOUR_MONTH_CUSTOMER_CURRENCY_CPC_SPEND]: {
    id: "Metric Name: FOUR_MONTH_CUSTOMER_CURRENCY_CPC_SPEND",
    defaultMessage: "Four Month CPC Spend",
  },
  [MetricTypes.FOUR_MONTH_CAC]: { id: "Metric Name: FOUR_MONTH_CAC", defaultMessage: "Four Month CAC" },
  [MetricTypes.FIVE_MONTH_AOV]: { id: "Metric Name: FIVE_MONTH_AOV", defaultMessage: "Five Month AOV" },
  [MetricTypes.FIVE_MONTH_FREQUENCY]: {
    id: "Metric Name: FIVE_MONTH_FREQUENCY",
    defaultMessage: "Five Month Frequency",
  },
  [MetricTypes.FIVE_MONTH_LTV]: { id: "Metric Name: FIVE_MONTH_LTV", defaultMessage: "Five Month LTV" },
  [MetricTypes.FIVE_MONTH_NET_REVENUE]: {
    id: "Metric Name: FIVE_MONTH_NET_REVENUE",
    defaultMessage: "Five Month Net Revenue",
  },
  [MetricTypes.FIVE_MONTH_UNIQUE_CUSTOMER_COUNT]: {
    id: "Metric Name: FIVE_MONTH_UNIQUE_CUSTOMER_COUNT",
    defaultMessage: "Five Month Unique Customer Count",
  },
  [MetricTypes.FIVE_MONTH_ORDERS]: { id: "Metric Name: FIVE_MONTH_ORDERS", defaultMessage: "Five Month Orders" },
  [MetricTypes.FIVE_MONTH_CUSTOMER_CURRENCY_CPC_SPEND]: {
    id: "Metric Name: FIVE_MONTH_CUSTOMER_CURRENCY_CPC_SPEND",
    defaultMessage: "Five Month CPC Spend",
  },
  [MetricTypes.FIVE_MONTH_CAC]: { id: "Metric Name: FIVE_MONTH_CAC", defaultMessage: "Five Month CAC" },
  [MetricTypes.SIX_MONTH_AOV]: { id: "Metric Name: SIX_MONTH_AOV", defaultMessage: "Six Month AOV" },
  [MetricTypes.SIX_MONTH_FREQUENCY]: { id: "Metric Name: SIX_MONTH_FREQUENCY", defaultMessage: "Six Month Frequency" },
  [MetricTypes.SIX_MONTH_LTV]: { id: "Metric Name: SIX_MONTH_LTV", defaultMessage: "Six Month LTV" },
  [MetricTypes.SIX_MONTH_NET_REVENUE]: {
    id: "Metric Name: SIX_MONTH_NET_REVENUE",
    defaultMessage: "Six Month Net Revenue",
  },
  [MetricTypes.SIX_MONTH_UNIQUE_CUSTOMER_COUNT]: {
    id: "Metric Name: SIX_MONTH_UNIQUE_CUSTOMER_COUNT",
    defaultMessage: "Six Month Unique Customer Count",
  },
  [MetricTypes.SIX_MONTH_ORDERS]: { id: "Metric Name: SIX_MONTH_ORDERS", defaultMessage: "Six Month Orders" },
  [MetricTypes.SIX_MONTH_CUSTOMER_CURRENCY_CPC_SPEND]: {
    id: "Metric Name: SIX_MONTH_CUSTOMER_CURRENCY_CPC_SPEND",
    defaultMessage: "Six Month CPC Spend",
  },
  [MetricTypes.SIX_MONTH_CAC]: { id: "Metric Name: SIX_MONTH_CAC", defaultMessage: "Six Month CAC" },
  [MetricTypes.SEVEN_MONTH_AOV]: { id: "Metric Name: SEVEN_MONTH_AOV", defaultMessage: "Seven Month AOV" },
  [MetricTypes.SEVEN_MONTH_FREQUENCY]: {
    id: "Metric Name: SEVEN_MONTH_FREQUENCY",
    defaultMessage: "Seven Month Frequency",
  },
  [MetricTypes.SEVEN_MONTH_LTV]: { id: "Metric Name: SEVEN_MONTH_LTV", defaultMessage: "Seven Month LTV" },
  [MetricTypes.SEVEN_MONTH_NET_REVENUE]: {
    id: "Metric Name: SEVEN_MONTH_NET_REVENUE",
    defaultMessage: "Seven Month Net Revenue",
  },
  [MetricTypes.SEVEN_MONTH_UNIQUE_CUSTOMER_COUNT]: {
    id: "Metric Name: SEVEN_MONTH_UNIQUE_CUSTOMER_COUNT",
    defaultMessage: "Seven Month Unique Customer Count",
  },
  [MetricTypes.SEVEN_MONTH_ORDERS]: { id: "Metric Name: SEVEN_MONTH_ORDERS", defaultMessage: "Seven Month Orders" },
  [MetricTypes.SEVEN_MONTH_CUSTOMER_CURRENCY_CPC_SPEND]: {
    id: "Metric Name: SEVEN_MONTH_CUSTOMER_CURRENCY_CPC_SPEND",
    defaultMessage: "Seven Month CPC Spend",
  },
  [MetricTypes.SEVEN_MONTH_CAC]: { id: "Metric Name: SEVEN_MONTH_CAC", defaultMessage: "Seven Month CAC" },
  [MetricTypes.EIGHT_MONTH_AOV]: { id: "Metric Name: EIGHT_MONTH_AOV", defaultMessage: "Eight Month AOV" },
  [MetricTypes.EIGHT_MONTH_FREQUENCY]: {
    id: "Metric Name: EIGHT_MONTH_FREQUENCY",
    defaultMessage: "Eight Month Frequency",
  },
  [MetricTypes.EIGHT_MONTH_LTV]: { id: "Metric Name: EIGHT_MONTH_LTV", defaultMessage: "Eight Month LTV" },
  [MetricTypes.EIGHT_MONTH_NET_REVENUE]: {
    id: "Metric Name: EIGHT_MONTH_NET_REVENUE",
    defaultMessage: "Eight Month Net Revenue",
  },
  [MetricTypes.EIGHT_MONTH_UNIQUE_CUSTOMER_COUNT]: {
    id: "Metric Name: EIGHT_MONTH_UNIQUE_CUSTOMER_COUNT",
    defaultMessage: "Eight Month Unique Customer Count",
  },
  [MetricTypes.EIGHT_MONTH_ORDERS]: { id: "Metric Name: EIGHT_MONTH_ORDERS", defaultMessage: "Eight Month Orders" },
  [MetricTypes.EIGHT_MONTH_CUSTOMER_CURRENCY_CPC_SPEND]: {
    id: "Metric Name: EIGHT_MONTH_CUSTOMER_CURRENCY_CPC_SPEND",
    defaultMessage: "Eight Month CPC Spend",
  },
  [MetricTypes.EIGHT_MONTH_CAC]: { id: "Metric Name: EIGHT_MONTH_CAC", defaultMessage: "Eight Month CAC" },
  [MetricTypes.NINE_MONTH_AOV]: { id: "Metric Name: NINE_MONTH_AOV", defaultMessage: "Nine Month AOV" },
  [MetricTypes.NINE_MONTH_FREQUENCY]: {
    id: "Metric Name: NINE_MONTH_FREQUENCY",
    defaultMessage: "Nine Month Frequency",
  },
  [MetricTypes.NINE_MONTH_LTV]: { id: "Metric Name: NINE_MONTH_LTV", defaultMessage: "Nine Month LTV" },
  [MetricTypes.NINE_MONTH_NET_REVENUE]: {
    id: "Metric Name: NINE_MONTH_NET_REVENUE",
    defaultMessage: "Nine Month Net Revenue",
  },
  [MetricTypes.NINE_MONTH_UNIQUE_CUSTOMER_COUNT]: {
    id: "Metric Name: NINE_MONTH_UNIQUE_CUSTOMER_COUNT",
    defaultMessage: "Nine Month Unique Customer Count",
  },
  [MetricTypes.NINE_MONTH_ORDERS]: { id: "Metric Name: NINE_MONTH_ORDERS", defaultMessage: "Nine Month Orders" },
  [MetricTypes.NINE_MONTH_CUSTOMER_CURRENCY_CPC_SPEND]: {
    id: "Metric Name: NINE_MONTH_CUSTOMER_CURRENCY_CPC_SPEND",
    defaultMessage: "Nine Month CPC Spend",
  },
  [MetricTypes.NINE_MONTH_CAC]: { id: "Metric Name: NINE_MONTH_CAC", defaultMessage: "Nine Month CAC" },
  [MetricTypes.TEN_MONTH_AOV]: { id: "Metric Name: TEN_MONTH_AOV", defaultMessage: "Ten Month AOV" },
  [MetricTypes.TEN_MONTH_FREQUENCY]: { id: "Metric Name: TEN_MONTH_FREQUENCY", defaultMessage: "Ten Month Frequency" },
  [MetricTypes.TEN_MONTH_LTV]: { id: "Metric Name: TEN_MONTH_LTV", defaultMessage: "Ten Month LTV" },
  [MetricTypes.TEN_MONTH_NET_REVENUE]: {
    id: "Metric Name: TEN_MONTH_NET_REVENUE",
    defaultMessage: "Ten Month Net Revenue",
  },
  [MetricTypes.TEN_MONTH_UNIQUE_CUSTOMER_COUNT]: {
    id: "Metric Name: TEN_MONTH_UNIQUE_CUSTOMER_COUNT",
    defaultMessage: "Ten Month Unique Customer Count",
  },
  [MetricTypes.TEN_MONTH_ORDERS]: { id: "Metric Name: TEN_MONTH_ORDERS", defaultMessage: "Ten Month Orders" },
  [MetricTypes.TEN_MONTH_CUSTOMER_CURRENCY_CPC_SPEND]: {
    id: "Metric Name: TEN_MONTH_CUSTOMER_CURRENCY_CPC_SPEND",
    defaultMessage: "Ten Month CPC Spend",
  },
  [MetricTypes.TEN_MONTH_CAC]: { id: "Metric Name: TEN_MONTH_CAC", defaultMessage: "Ten Month CAC" },
  [MetricTypes.ELEVEN_MONTH_AOV]: { id: "Metric Name: ELEVEN_MONTH_AOV", defaultMessage: "Eleven Month AOV" },
  [MetricTypes.ELEVEN_MONTH_FREQUENCY]: {
    id: "Metric Name: ELEVEN_MONTH_FREQUENCY",
    defaultMessage: "Eleven Month Frequency",
  },
  [MetricTypes.ELEVEN_MONTH_LTV]: { id: "Metric Name: ELEVEN_MONTH_LTV", defaultMessage: "Eleven Month LTV" },
  [MetricTypes.ELEVEN_MONTH_NET_REVENUE]: {
    id: "Metric Name: ELEVEN_MONTH_NET_REVENUE",
    defaultMessage: "Eleven Month Net Revenue",
  },
  [MetricTypes.ELEVEN_MONTH_UNIQUE_CUSTOMER_COUNT]: {
    id: "Metric Name: ELEVEN_MONTH_UNIQUE_CUSTOMER_COUNT",
    defaultMessage: "Eleven Month Unique Customer Count",
  },
  [MetricTypes.ELEVEN_MONTH_ORDERS]: { id: "Metric Name: ELEVEN_MONTH_ORDERS", defaultMessage: "Eleven Month Orders" },
  [MetricTypes.ELEVEN_MONTH_CUSTOMER_CURRENCY_CPC_SPEND]: {
    id: "Metric Name: ELEVEN_MONTH_CUSTOMER_CURRENCY_CPC_SPEND",
    defaultMessage: "Eleven Month CPC Spend",
  },
  [MetricTypes.ELEVEN_MONTH_CAC]: { id: "Metric Name: ELEVEN_MONTH_CAC", defaultMessage: "Eleven Month CAC" },
  [MetricTypes.TWELVE_MONTH_AOV]: { id: "Metric Name: TWELVE_MONTH_AOV", defaultMessage: "Twelve Month AOV" },
  [MetricTypes.TWELVE_MONTH_FREQUENCY]: {
    id: "Metric Name: TWELVE_MONTH_FREQUENCY",
    defaultMessage: "Twelve Month Frequency",
  },
  [MetricTypes.TWELVE_MONTH_LTV]: { id: "Metric Name: TWELVE_MONTH_LTV", defaultMessage: "Twelve Month LTV" },
  [MetricTypes.TWELVE_MONTH_NET_REVENUE]: {
    id: "Metric Name: TWELVE_MONTH_NET_REVENUE",
    defaultMessage: "Twelve Month Net Revenue",
  },
  [MetricTypes.TWELVE_MONTH_UNIQUE_CUSTOMER_COUNT]: {
    id: "Metric Name: TWELVE_MONTH_UNIQUE_CUSTOMER_COUNT",
    defaultMessage: "Twelve Month Unique Customer Count",
  },
  [MetricTypes.TWELVE_MONTH_ORDERS]: { id: "Metric Name: TWELVE_MONTH_ORDERS", defaultMessage: "Twelve Month Orders" },
  [MetricTypes.TWELVE_MONTH_CUSTOMER_CURRENCY_CPC_SPEND]: {
    id: "Metric Name: TWELVE_MONTH_CUSTOMER_CURRENCY_CPC_SPEND",
    defaultMessage: "Twelve Month CPC Spend",
  },
  [MetricTypes.TWELVE_MONTH_CAC]: { id: "Metric Name: TWELVE_MONTH_CAC", defaultMessage: "Twelve Month CAC" },
  [MetricTypes.THIRTEEN_MONTH_AOV]: { id: "Metric Name: THIRTEEN_MONTH_AOV", defaultMessage: "Thirteen Month AOV" },
  [MetricTypes.THIRTEEN_MONTH_FREQUENCY]: {
    id: "Metric Name: THIRTEEN_MONTH_FREQUENCY",
    defaultMessage: "Thirteen Month Frequency",
  },
  [MetricTypes.THIRTEEN_MONTH_LTV]: { id: "Metric Name: THIRTEEN_MONTH_LTV", defaultMessage: "Thirteen Month LTV" },
  [MetricTypes.THIRTEEN_MONTH_NET_REVENUE]: {
    id: "Metric Name: THIRTEEN_MONTH_NET_REVENUE",
    defaultMessage: "Thirteen Month Net Revenue",
  },
  [MetricTypes.THIRTEEN_MONTH_UNIQUE_CUSTOMER_COUNT]: {
    id: "Metric Name: THIRTEEN_MONTH_UNIQUE_CUSTOMER_COUNT",
    defaultMessage: "Thirteen Month Unique Customer Count",
  },
  [MetricTypes.THIRTEEN_MONTH_ORDERS]: {
    id: "Metric Name: THIRTEEN_MONTH_ORDERS",
    defaultMessage: "Thirteen Month Orders",
  },
  [MetricTypes.THIRTEEN_MONTH_CUSTOMER_CURRENCY_CPC_SPEND]: {
    id: "Metric Name: THIRTEEN_MONTH_CUSTOMER_CURRENCY_CPC_SPEND",
    defaultMessage: "Thirteen Month CPC Spend",
  },
  [MetricTypes.THIRTEEN_MONTH_CAC]: { id: "Metric Name: THIRTEEN_MONTH_CAC", defaultMessage: "Thirteen Month CAC" },
  [MetricTypes.FOURTEEN_MONTH_AOV]: { id: "Metric Name: FOURTEEN_MONTH_AOV", defaultMessage: "Fourteen Month AOV" },
  [MetricTypes.FOURTEEN_MONTH_FREQUENCY]: {
    id: "Metric Name: FOURTEEN_MONTH_FREQUENCY",
    defaultMessage: "Fourteen Month Frequency",
  },
  [MetricTypes.FOURTEEN_MONTH_LTV]: { id: "Metric Name: FOURTEEN_MONTH_LTV", defaultMessage: "Fourteen Month LTV" },
  [MetricTypes.FOURTEEN_MONTH_NET_REVENUE]: {
    id: "Metric Name: FOURTEEN_MONTH_NET_REVENUE",
    defaultMessage: "Fourteen Month Net Revenue",
  },
  [MetricTypes.FOURTEEN_MONTH_UNIQUE_CUSTOMER_COUNT]: {
    id: "Metric Name: FOURTEEN_MONTH_UNIQUE_CUSTOMER_COUNT",
    defaultMessage: "Fourteen Month Unique Customer Count",
  },
  [MetricTypes.FOURTEEN_MONTH_ORDERS]: {
    id: "Metric Name: FOURTEEN_MONTH_ORDERS",
    defaultMessage: "Fourteen Month Orders",
  },
  [MetricTypes.FOURTEEN_MONTH_CUSTOMER_CURRENCY_CPC_SPEND]: {
    id: "Metric Name: FOURTEEN_MONTH_CUSTOMER_CURRENCY_CPC_SPEND",
    defaultMessage: "Fourteen Month CPC Spend",
  },
  [MetricTypes.FOURTEEN_MONTH_CAC]: { id: "Metric Name: FOURTEEN_MONTH_CAC", defaultMessage: "Fourteen Month CAC" },
  [MetricTypes.FIFTEEN_MONTH_AOV]: { id: "Metric Name: FIFTEEN_MONTH_AOV", defaultMessage: "Fifteen Month AOV" },
  [MetricTypes.FIFTEEN_MONTH_FREQUENCY]: {
    id: "Metric Name: FIFTEEN_MONTH_FREQUENCY",
    defaultMessage: "Fifteen Month Frequency",
  },
  [MetricTypes.FIFTEEN_MONTH_LTV]: { id: "Metric Name: FIFTEEN_MONTH_LTV", defaultMessage: "Fifteen Month LTV" },
  [MetricTypes.FIFTEEN_MONTH_NET_REVENUE]: {
    id: "Metric Name: FIFTEEN_MONTH_NET_REVENUE",
    defaultMessage: "Fifteen Month Net Revenue",
  },
  [MetricTypes.FIFTEEN_MONTH_UNIQUE_CUSTOMER_COUNT]: {
    id: "Metric Name: FIFTEEN_MONTH_UNIQUE_CUSTOMER_COUNT",
    defaultMessage: "Fifteen Month Unique Customer Count",
  },
  [MetricTypes.FIFTEEN_MONTH_ORDERS]: {
    id: "Metric Name: FIFTEEN_MONTH_ORDERS",
    defaultMessage: "Fifteen Month Orders",
  },
  [MetricTypes.FIFTEEN_MONTH_CUSTOMER_CURRENCY_CPC_SPEND]: {
    id: "Metric Name: FIFTEEN_MONTH_CUSTOMER_CURRENCY_CPC_SPEND",
    defaultMessage: "Fifteen Month CPC Spend",
  },
  [MetricTypes.FIFTEEN_MONTH_CAC]: { id: "Metric Name: FIFTEEN_MONTH_CAC", defaultMessage: "Fifteen Month CAC" },
  [MetricTypes.SIXTEEN_MONTH_AOV]: { id: "Metric Name: SIXTEEN_MONTH_AOV", defaultMessage: "Sixteen Month AOV" },
  [MetricTypes.SIXTEEN_MONTH_FREQUENCY]: {
    id: "Metric Name: SIXTEEN_MONTH_FREQUENCY",
    defaultMessage: "Sixteen Month Frequency",
  },
  [MetricTypes.SIXTEEN_MONTH_LTV]: { id: "Metric Name: SIXTEEN_MONTH_LTV", defaultMessage: "Sixteen Month LTV" },
  [MetricTypes.SIXTEEN_MONTH_NET_REVENUE]: {
    id: "Metric Name: SIXTEEN_MONTH_NET_REVENUE",
    defaultMessage: "Sixteen Month Net Revenue",
  },
  [MetricTypes.SIXTEEN_MONTH_UNIQUE_CUSTOMER_COUNT]: {
    id: "Metric Name: SIXTEEN_MONTH_UNIQUE_CUSTOMER_COUNT",
    defaultMessage: "Sixteen Month Unique Customer Count",
  },
  [MetricTypes.SIXTEEN_MONTH_ORDERS]: {
    id: "Metric Name: SIXTEEN_MONTH_ORDERS",
    defaultMessage: "Sixteen Month Orders",
  },
  [MetricTypes.SIXTEEN_MONTH_CUSTOMER_CURRENCY_CPC_SPEND]: {
    id: "Metric Name: SIXTEEN_MONTH_CUSTOMER_CURRENCY_CPC_SPEND",
    defaultMessage: "Sixteen Month CPC Spend",
  },
  [MetricTypes.SIXTEEN_MONTH_CAC]: { id: "Metric Name: SIXTEEN_MONTH_CAC", defaultMessage: "Sixteen Month CAC" },
  [MetricTypes.SEVENTEEN_MONTH_AOV]: { id: "Metric Name: SEVENTEEN_MONTH_AOV", defaultMessage: "Seventeen Month AOV" },
  [MetricTypes.SEVENTEEN_MONTH_FREQUENCY]: {
    id: "Metric Name: SEVENTEEN_MONTH_FREQUENCY",
    defaultMessage: "Seventeen Month Frequency",
  },
  [MetricTypes.SEVENTEEN_MONTH_LTV]: { id: "Metric Name: SEVENTEEN_MONTH_LTV", defaultMessage: "Seventeen Month LTV" },
  [MetricTypes.SEVENTEEN_MONTH_NET_REVENUE]: {
    id: "Metric Name: SEVENTEEN_MONTH_NET_REVENUE",
    defaultMessage: "Seventeen Month Net Revenue",
  },
  [MetricTypes.SEVENTEEN_MONTH_UNIQUE_CUSTOMER_COUNT]: {
    id: "Metric Name: SEVENTEEN_MONTH_UNIQUE_CUSTOMER_COUNT",
    defaultMessage: "Seventeen Month Unique Customer Count",
  },
  [MetricTypes.SEVENTEEN_MONTH_ORDERS]: {
    id: "Metric Name: SEVENTEEN_MONTH_ORDERS",
    defaultMessage: "Seventeen Month Orders",
  },
  [MetricTypes.SEVENTEEN_MONTH_CUSTOMER_CURRENCY_CPC_SPEND]: {
    id: "Metric Name: SEVENTEEN_MONTH_CUSTOMER_CURRENCY_CPC_SPEND",
    defaultMessage: "Seventeen Month CPC Spend",
  },
  [MetricTypes.SEVENTEEN_MONTH_CAC]: { id: "Metric Name: SEVENTEEN_MONTH_CAC", defaultMessage: "Seventeen Month CAC" },
  [MetricTypes.EIGHTEEN_MONTH_AOV]: { id: "Metric Name: EIGHTEEN_MONTH_AOV", defaultMessage: "Eighteen Month AOV" },
  [MetricTypes.EIGHTEEN_MONTH_FREQUENCY]: {
    id: "Metric Name: EIGHTEEN_MONTH_FREQUENCY",
    defaultMessage: "Eighteen Month Frequency",
  },
  [MetricTypes.EIGHTEEN_MONTH_LTV]: { id: "Metric Name: EIGHTEEN_MONTH_LTV", defaultMessage: "Eighteen Month LTV" },
  [MetricTypes.EIGHTEEN_MONTH_NET_REVENUE]: {
    id: "Metric Name: EIGHTEEN_MONTH_NET_REVENUE",
    defaultMessage: "Eighteen Month Net Revenue",
  },
  [MetricTypes.EIGHTEEN_MONTH_UNIQUE_CUSTOMER_COUNT]: {
    id: "Metric Name: EIGHTEEN_MONTH_UNIQUE_CUSTOMER_COUNT",
    defaultMessage: "Eighteen Month Unique Customer Count",
  },
  [MetricTypes.EIGHTEEN_MONTH_ORDERS]: {
    id: "Metric Name: EIGHTEEN_MONTH_ORDERS",
    defaultMessage: "Eighteen Month Orders",
  },
  [MetricTypes.EIGHTEEN_MONTH_CUSTOMER_CURRENCY_CPC_SPEND]: {
    id: "Metric Name: EIGHTEEN_MONTH_CUSTOMER_CURRENCY_CPC_SPEND",
    defaultMessage: "Eighteen Month CPC Spend",
  },
  [MetricTypes.EIGHTEEN_MONTH_CAC]: { id: "Metric Name: EIGHTEEN_MONTH_CAC", defaultMessage: "Eighteen Month CAC" },
  [MetricTypes.NINETEEN_MONTH_AOV]: { id: "Metric Name: NINETEEN_MONTH_AOV", defaultMessage: "Nineteen Month AOV" },
  [MetricTypes.NINETEEN_MONTH_FREQUENCY]: {
    id: "Metric Name: NINETEEN_MONTH_FREQUENCY",
    defaultMessage: "Nineteen Month Frequency",
  },
  [MetricTypes.NINETEEN_MONTH_LTV]: { id: "Metric Name: NINETEEN_MONTH_LTV", defaultMessage: "Nineteen Month LTV" },
  [MetricTypes.NINETEEN_MONTH_NET_REVENUE]: {
    id: "Metric Name: NINETEEN_MONTH_NET_REVENUE",
    defaultMessage: "Nineteen Month Net Revenue",
  },
  [MetricTypes.NINETEEN_MONTH_UNIQUE_CUSTOMER_COUNT]: {
    id: "Metric Name: NINETEEN_MONTH_UNIQUE_CUSTOMER_COUNT",
    defaultMessage: "Nineteen Month Unique Customer Count",
  },
  [MetricTypes.NINETEEN_MONTH_ORDERS]: {
    id: "Metric Name: NINETEEN_MONTH_ORDERS",
    defaultMessage: "Nineteen Month Orders",
  },
  [MetricTypes.NINETEEN_MONTH_CUSTOMER_CURRENCY_CPC_SPEND]: {
    id: "Metric Name: NINETEEN_MONTH_CUSTOMER_CURRENCY_CPC_SPEND",
    defaultMessage: "Nineteen Month CPC Spend",
  },
  [MetricTypes.NINETEEN_MONTH_CAC]: { id: "Metric Name: NINETEEN_MONTH_CAC", defaultMessage: "Nineteen Month CAC" },
  [MetricTypes.TWENTY_MONTH_AOV]: { id: "Metric Name: TWENTY_MONTH_AOV", defaultMessage: "Twenty Month AOV" },
  [MetricTypes.TWENTY_MONTH_FREQUENCY]: {
    id: "Metric Name: TWENTY_MONTH_FREQUENCY",
    defaultMessage: "Twenty Month Frequency",
  },
  [MetricTypes.TWENTY_MONTH_LTV]: { id: "Metric Name: TWENTY_MONTH_LTV", defaultMessage: "Twenty Month LTV" },
  [MetricTypes.TWENTY_MONTH_NET_REVENUE]: {
    id: "Metric Name: TWENTY_MONTH_NET_REVENUE",
    defaultMessage: "Twenty Month Net Revenue",
  },
  [MetricTypes.TWENTY_MONTH_UNIQUE_CUSTOMER_COUNT]: {
    id: "Metric Name: TWENTY_MONTH_UNIQUE_CUSTOMER_COUNT",
    defaultMessage: "Twenty Month Unique Customer Count",
  },
  [MetricTypes.TWENTY_MONTH_ORDERS]: { id: "Metric Name: TWENTY_MONTH_ORDERS", defaultMessage: "Twenty Month Orders" },
  [MetricTypes.TWENTY_MONTH_CUSTOMER_CURRENCY_CPC_SPEND]: {
    id: "Metric Name: TWENTY_MONTH_CUSTOMER_CURRENCY_CPC_SPEND",
    defaultMessage: "Twenty Month CPC Spend",
  },
  [MetricTypes.TWENTY_MONTH_CAC]: { id: "Metric Name: TWENTY_MONTH_CAC", defaultMessage: "Twenty Month CAC" },
  [MetricTypes.TWENTY_ONE_MONTH_AOV]: {
    id: "Metric Name: TWENTY_ONE_MONTH_AOV",
    defaultMessage: "Twenty-One Month AOV",
  },
  [MetricTypes.TWENTY_ONE_MONTH_FREQUENCY]: {
    id: "Metric Name: TWENTY_ONE_MONTH_FREQUENCY",
    defaultMessage: "Twenty-One Month Frequency",
  },
  [MetricTypes.TWENTY_ONE_MONTH_LTV]: {
    id: "Metric Name: TWENTY_ONE_MONTH_LTV",
    defaultMessage: "Twenty-One Month LTV",
  },
  [MetricTypes.TWENTY_ONE_MONTH_NET_REVENUE]: {
    id: "Metric Name: TWENTY_ONE_MONTH_NET_REVENUE",
    defaultMessage: "Twenty-One Month Net Revenue",
  },
  [MetricTypes.TWENTY_ONE_MONTH_UNIQUE_CUSTOMER_COUNT]: {
    id: "Metric Name: TWENTY_ONE_MONTH_UNIQUE_CUSTOMER_COUNT",
    defaultMessage: "Twenty-One Month Unique Customer Count",
  },
  [MetricTypes.TWENTY_ONE_MONTH_ORDERS]: {
    id: "Metric Name: TWENTY_ONE_MONTH_ORDERS",
    defaultMessage: "Twenty-One Month Orders",
  },
  [MetricTypes.TWENTY_ONE_MONTH_CUSTOMER_CURRENCY_CPC_SPEND]: {
    id: "Metric Name: TWENTY_ONE_MONTH_CUSTOMER_CURRENCY_CPC_SPEND",
    defaultMessage: "Twenty-One Month CPC Spend",
  },
  [MetricTypes.TWENTY_ONE_MONTH_CAC]: {
    id: "Metric Name: TWENTY_ONE_MONTH_CAC",
    defaultMessage: "Twenty-One Month CAC",
  },
  [MetricTypes.TWENTY_TWO_MONTH_AOV]: {
    id: "Metric Name: TWENTY_TWO_MONTH_AOV",
    defaultMessage: "Twenty-Two Month AOV",
  },
  [MetricTypes.TWENTY_TWO_MONTH_FREQUENCY]: {
    id: "Metric Name: TWENTY_TWO_MONTH_FREQUENCY",
    defaultMessage: "Twenty-Two Month Frequency",
  },
  [MetricTypes.TWENTY_TWO_MONTH_LTV]: {
    id: "Metric Name: TWENTY_TWO_MONTH_LTV",
    defaultMessage: "Twenty-Two Month LTV",
  },
  [MetricTypes.TWENTY_TWO_MONTH_NET_REVENUE]: {
    id: "Metric Name: TWENTY_TWO_MONTH_NET_REVENUE",
    defaultMessage: "Twenty-Two Month Net Revenue",
  },
  [MetricTypes.TWENTY_TWO_MONTH_UNIQUE_CUSTOMER_COUNT]: {
    id: "Metric Name: TWENTY_TWO_MONTH_UNIQUE_CUSTOMER_COUNT",
    defaultMessage: "Twenty-Two Month Unique Customer Count",
  },
  [MetricTypes.TWENTY_TWO_MONTH_ORDERS]: {
    id: "Metric Name: TWENTY_TWO_MONTH_ORDERS",
    defaultMessage: "Twenty-Two Month Orders",
  },
  [MetricTypes.TWENTY_TWO_MONTH_CUSTOMER_CURRENCY_CPC_SPEND]: {
    id: "Metric Name: TWENTY_TWO_MONTH_CUSTOMER_CURRENCY_CPC_SPEND",
    defaultMessage: "Twenty-Two Month CPC Spend",
  },
  [MetricTypes.TWENTY_TWO_MONTH_CAC]: {
    id: "Metric Name: TWENTY_TWO_MONTH_CAC",
    defaultMessage: "Twenty-Two Month CAC",
  },
  [MetricTypes.TWENTY_THREE_MONTH_AOV]: {
    id: "Metric Name: TWENTY_THREE_MONTH_AOV",
    defaultMessage: "Twenty-Three Month AOV",
  },
  [MetricTypes.TWENTY_THREE_MONTH_FREQUENCY]: {
    id: "Metric Name: TWENTY_THREE_MONTH_FREQUENCY",
    defaultMessage: "Twenty-Three Month Frequency",
  },
  [MetricTypes.TWENTY_THREE_MONTH_LTV]: {
    id: "Metric Name: TWENTY_THREE_MONTH_LTV",
    defaultMessage: "Twenty-Three Month LTV",
  },
  [MetricTypes.TWENTY_THREE_MONTH_NET_REVENUE]: {
    id: "Metric Name: TWENTY_THREE_MONTH_NET_REVENUE",
    defaultMessage: "Twenty-Three Month Net Revenue",
  },
  [MetricTypes.TWENTY_THREE_MONTH_UNIQUE_CUSTOMER_COUNT]: {
    id: "Metric Name: TWENTY_THREE_MONTH_UNIQUE_CUSTOMER_COUNT",
    defaultMessage: "Twenty-Three Month Unique Customer Count",
  },
  [MetricTypes.TWENTY_THREE_MONTH_ORDERS]: {
    id: "Metric Name: TWENTY_THREE_MONTH_ORDERS",
    defaultMessage: "Twenty-Three Month Orders",
  },
  [MetricTypes.TWENTY_THREE_MONTH_CUSTOMER_CURRENCY_CPC_SPEND]: {
    id: "Metric Name: TWENTY_THREE_MONTH_CUSTOMER_CURRENCY_CPC_SPEND",
    defaultMessage: "Twenty-Three Month CPC Spend",
  },
  [MetricTypes.TWENTY_THREE_MONTH_CAC]: {
    id: "Metric Name: TWENTY_THREE_MONTH_CAC",
    defaultMessage: "Twenty-Three Month CAC",
  },
  [MetricTypes.TWENTY_FOUR_MONTH_AOV]: {
    id: "Metric Name: TWENTY_FOUR_MONTH_AOV",
    defaultMessage: "Twenty-Four Month AOV",
  },
  [MetricTypes.TWENTY_FOUR_MONTH_FREQUENCY]: {
    id: "Metric Name: TWENTY_FOUR_MONTH_FREQUENCY",
    defaultMessage: "Twenty-Four Month Frequency",
  },
  [MetricTypes.TWENTY_FOUR_MONTH_LTV]: {
    id: "Metric Name: TWENTY_FOUR_MONTH_LTV",
    defaultMessage: "Twenty-Four Month LTV",
  },
  [MetricTypes.TWENTY_FOUR_MONTH_NET_REVENUE]: {
    id: "Metric Name: TWENTY_FOUR_MONTH_NET_REVENUE",
    defaultMessage: "Twenty-Four Month Net Revenue",
  },
  [MetricTypes.TWENTY_FOUR_MONTH_UNIQUE_CUSTOMER_COUNT]: {
    id: "Metric Name: TWENTY_FOUR_MONTH_UNIQUE_CUSTOMER_COUNT",
    defaultMessage: "Twenty-Four Month Unique Customer Count",
  },
  [MetricTypes.TWENTY_FOUR_MONTH_ORDERS]: {
    id: "Metric Name: TWENTY_FOUR_MONTH_ORDERS",
    defaultMessage: "Twenty-Four Month Orders",
  },
  [MetricTypes.TWENTY_FOUR_MONTH_CUSTOMER_CURRENCY_CPC_SPEND]: {
    id: "Metric Name: TWENTY_FOUR_MONTH_CUSTOMER_CURRENCY_CPC_SPEND",
    defaultMessage: "Twenty-Four Month CPC Spend",
  },
  [MetricTypes.TWENTY_FOUR_MONTH_CAC]: {
    id: "Metric Name: TWENTY_FOUR_MONTH_CAC",
    defaultMessage: "Twenty-Four Month CAC",
  },
  [MetricTypes.META_AD_ACCOUNT_ID]: { id: "Metric Name: META_AD_ACCOUNT_ID", defaultMessage: "Meta ad account ID" },
  [MetricTypes.META_AD_ACCOUNT_NAME]: {
    id: "Metric Name: META_AD_ACCOUNT_NAME",
    defaultMessage: "Meta ad account name",
  },
  [MetricTypes.ADSET_FIRST_USED]: { id: "Metric Name: ADSET_FIRST_USED", defaultMessage: "First used" },
  [MetricTypes.ADSET_LAST_USED]: { id: "Metric Name: ADSET_LAST_USED", defaultMessage: "Last used" },
  [MetricTypes.ADSET_REGION]: { id: "Metric Name: ADSET_REGION", defaultMessage: "Region" },
  [MetricTypes.ADSET_NAME]: { id: "Metric Name: ADSET_NAME", defaultMessage: "Adset Name" },
  [MetricTypes.ADSET_AUDIENCE]: { id: "Metric Name: ADSET_AUDIENCE", defaultMessage: "Audience Type" },
  [MetricTypes.ADSET_IS_CATALOG]: { id: "Metric Name: ADSET_IS_CATALOG", defaultMessage: "Adset is catalog" },
  [MetricTypes.CAMPAIGN_OBJECTIVE]: { id: "Metric Name: CAMPAIGN_OBJECTIVE", defaultMessage: "Campaign Objective" },
  [MetricTypes.AUDIENCE_ENRICHMENT_ADSET_TARGETING]: {
    id: "Metric Name: AUDIENCE_ENRICHMENT_ADSET_TARGETING",
    defaultMessage: "Audience enrichment adset targeting",
  },
  [MetricTypes.ADSET_TARGETING]: { id: "Metric Name: ADSET_TARGETING", defaultMessage: "Adset targeting" },
  [MetricTypes.BENCHMARK_SAMPLE_SIZE]: {
    id: "Metric Name: BENCHMARK_SAMPLE_SIZE",
    defaultMessage: "Benchmark sample size",
  },
  [MetricTypes.BENCHMARKED_MER]: { id: "Metric Name: BENCHMARKED_MER", defaultMessage: "Benchmarked MER" },
  [MetricTypes.BENCHMARKED_AMER]: { id: "Metric Name: BENCHMARKED_AMER", defaultMessage: "Benchmarked AMER" },
  [MetricTypes.BENCHMARKED_BLENDED_AD_ATTRIBUTED_CONVERSION_ROAS]: {
    id: "Metric Name: BENCHMARKED_BLENDED_AD_ATTRIBUTED_CONVERSION_ROAS",
    defaultMessage: "Benchmarked blended ad attributed conversion ROAS",
  },
  [MetricTypes.BENCHMARKED_BLENDED_AD_COST_PER_IMPRESSION]: {
    id: "Metric Name: BENCHMARKED_BLENDED_AD_COST_PER_IMPRESSION",
    defaultMessage: "Benchmarked blended ad cost per impression",
  },
  [MetricTypes.BENCHMARKED_BLENDED_AD_CLICK_THROUGH_RATE]: {
    id: "Metric Name: BENCHMARKED_BLENDED_AD_CLICK_THROUGH_RATE",
    defaultMessage: "Benchmarked blended ad click through rate",
  },
  [MetricTypes.BENCHMARKED_BLENDED_AD_COST_PER_CLICK]: {
    id: "Metric Name: BENCHMARKED_BLENDED_AD_COST_PER_CLICK",
    defaultMessage: "Benchmarked blended ad cost per click",
  },
  [MetricTypes.BENCHMARKED_GOOGLE_AD_ATTRIBUTED_CONVERSION_ROAS]: {
    id: "Metric Name: BENCHMARKED_GOOGLE_AD_ATTRIBUTED_CONVERSION_ROAS",
    defaultMessage: "Benchmarked Google ad attributed conversion ROAS",
  },
  [MetricTypes.BENCHMARKED_GOOGLE_AD_COST_PER_IMPRESSION]: {
    id: "Metric Name: BENCHMARKED_GOOGLE_AD_COST_PER_IMPRESSION",
    defaultMessage: "Benchmarked Google ad cost per impression",
  },
  [MetricTypes.BENCHMARKED_GOOGLE_AD_CLICK_THROUGH_RATE]: {
    id: "Metric Name: BENCHMARKED_GOOGLE_AD_CLICK_THROUGH_RATE",
    defaultMessage: "Benchmarked Google ad click through rate",
  },
  [MetricTypes.BENCHMARKED_GOOGLE_AD_COST_PER_CLICK]: {
    id: "Metric Name: BENCHMARKED_GOOGLE_AD_COST_PER_CLICK",
    defaultMessage: "Benchmarked Google ad cost per click",
  },
  [MetricTypes.BENCHMARKED_GOOGLE_AD_RELATIVE_PERCENTAGE_SPEND]: {
    id: "Metric Name: BENCHMARKED_GOOGLE_AD_RELATIVE_PERCENTAGE_SPEND",
    defaultMessage: "Benchmarked Google ad relative percentage spend",
  },
  [MetricTypes.BENCHMARKED_META_AD_ATTRIBUTED_CONVERSION_ROAS]: {
    id: "Metric Name: BENCHMARKED_META_AD_ATTRIBUTED_CONVERSION_ROAS",
    defaultMessage: "Benchmarked Meta ad attributed conversion ROAS",
  },
  [MetricTypes.BENCHMARKED_META_AD_COST_PER_IMPRESSION]: {
    id: "Metric Name: BENCHMARKED_META_AD_COST_PER_IMPRESSION",
    defaultMessage: "Benchmarked Meta ad cost per impression",
  },
  [MetricTypes.BENCHMARKED_META_AD_CLICK_THROUGH_RATE]: {
    id: "Metric Name: BENCHMARKED_META_AD_CLICK_THROUGH_RATE",
    defaultMessage: "Benchmarked Meta ad click through rate",
  },
  [MetricTypes.BENCHMARKED_META_AD_COST_PER_CLICK]: {
    id: "Metric Name: BENCHMARKED_META_AD_COST_PER_CLICK",
    defaultMessage: "Benchmarked Meta ad cost per click",
  },
  [MetricTypes.BENCHMARKED_META_AD_RELATIVE_PERCENTAGE_SPEND]: {
    id: "Metric Name: BENCHMARKED_META_AD_RELATIVE_PERCENTAGE_SPEND",
    defaultMessage: "Benchmarked Meta ad relative percentage spend",
  },
  [MetricTypes.GROSS_PRODUCT_REVENUE_FROM_ORDER_PROPORTION_LESS_DISCOUNTS_LESS_REFUNDED_AMOUNT_FROM_ORDERS]: {
    id: "Metric Name: GROSS_PRODUCT_REVENUE_FROM_ORDER_PROPORTION_LESS_DISCOUNTS_LESS_REFUNDED_AMOUNT_FROM_ORDERS",
    defaultMessage: "Total Sales",
  },
  [MetricTypes.GROSS_PRODUCT_REVENUE_FROM_ORDER_PROPORTION_LESS_DISCOUNTS_LESS_REFUNDED_AMOUNT_FROM_REFUNDS]: {
    id: "Metric Name: GROSS_PRODUCT_REVENUE_FROM_ORDER_PROPORTION_LESS_DISCOUNTS_LESS_REFUNDED_AMOUNT_FROM_REFUNDS",
    defaultMessage: "Total Sales",
  },
  [MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_LESS_REFUNDS_BY_REFUNDED_DATE_PLUS_SHIPPING_PLUS_TAX]: {
    id: "Metric Name: GROSS_REVENUE_LESS_DISCOUNTS_LESS_REFUNDS_BY_REFUNDED_DATE_PLUS_SHIPPING_PLUS_TAX",
    defaultMessage: "Total Sales",
  },
});

/**
 * Tooltip copy, where any has been written.
 *
 * Partial by design. A metric missing here previously rendered its raw lookup key to the user
 * (`"<metric>_TOOLTIP"`), because the Django gettext catalog returns its input when a string is untranslated.
 */
const metricTooltips: Partial<Record<MetricTypes, MessageDescriptor>> = defineMessages({
  [MetricTypes.CAC]: {
    id: "Metric Tooltip: CAC",
    defaultMessage: "Total Marketing spend divided by the number of new customers acquired through the selected period",
  },
  [MetricTypes.AD_CLICKS]: { id: "Metric Tooltip: AD_CLICKS", defaultMessage: "All clicks on your ad" },
  [MetricTypes.AD_FREQUENCY]: {
    id: "Metric Tooltip: AD_FREQUENCY",
    defaultMessage: "Average number of times a unique individual sees your ad (Impressions/Reach)",
  },
  [MetricTypes.AD_IMPRESSIONS]: {
    id: "Metric Tooltip: AD_IMPRESSIONS",
    defaultMessage: "How many times your ad was displayed on a screen",
  },
  [MetricTypes.AD_REACH]: {
    id: "Metric Tooltip: AD_REACH",
    defaultMessage: "Number of unique individuals who saw your ad",
  },
  [MetricTypes.AD_SPEND]: {
    id: "Metric Tooltip: AD_SPEND",
    defaultMessage: "Marketing expense is comprised of those costs incurred to present goods to prospective customers",
  },
  [MetricTypes.AD_CTR]: { id: "Metric Tooltip: AD_CTR", defaultMessage: "The ratio of sessions that result in clicks" },
  [MetricTypes.AD_AOV]: {
    id: "Metric Tooltip: AD_AOV",
    defaultMessage: "The average value of orders made through this platform",
  },
  [MetricTypes.AD_CONVERSION_RATE]: {
    id: "Metric Tooltip: AD_CONVERSION_RATE",
    defaultMessage: "The percentage of clicks that eventually result in a conversion",
  },
  [MetricTypes.AD_COST_PER_CONVERSION]: {
    id: "Metric Tooltip: AD_COST_PER_CONVERSION",
    defaultMessage: "The cost per conversion event",
  },
  [MetricTypes.AD_ATTRIBUTED_CONVERSION_ROAS]: {
    id: "Metric Tooltip: AD_ATTRIBUTED_CONVERSION_ROAS",
    defaultMessage: "Total revenue attributed to your ad divided by total amount spent on ad (Revenue/Spend)",
  },
  [MetricTypes.AD_ATTRIBUTED_CONVERSION_VALUE]: {
    id: "Metric Tooltip: AD_ATTRIBUTED_CONVERSION_VALUE",
    defaultMessage: "Total revenue attributed to this segment",
  },
  [MetricTypes.AD_ATTRIBUTED_CONVERSION_COUNT]: {
    id: "Metric Tooltip: AD_ATTRIBUTED_CONVERSION_COUNT",
    defaultMessage: "The total number of conversion events reported by the ad platform",
  },
  [MetricTypes.META_AD_CLICKS]: { id: "Metric Tooltip: META_AD_CLICKS", defaultMessage: "All clicks on your ad" },
  [MetricTypes.META_AD_FREQUENCY]: {
    id: "Metric Tooltip: META_AD_FREQUENCY",
    defaultMessage: "Average number of times a unique individual sees your ad (Impressions/Reach)",
  },
  [MetricTypes.META_AD_IMPRESSIONS]: {
    id: "Metric Tooltip: META_AD_IMPRESSIONS",
    defaultMessage: "How many times your ad was displayed on a screen",
  },
  [MetricTypes.META_AD_REACH]: {
    id: "Metric Tooltip: META_AD_REACH",
    defaultMessage: "Number of unique individuals who saw your ad",
  },
  [MetricTypes.META_AD_SPEND]: {
    id: "Metric Tooltip: META_AD_SPEND",
    defaultMessage: "The amount spent on the adset in this window of time",
  },
  [MetricTypes.META_AD_CTR]: {
    id: "Metric Tooltip: META_AD_CTR",
    defaultMessage: "The proportion of impressions that resulted in a click",
  },
  [MetricTypes.META_AD_AOV]: {
    id: "Metric Tooltip: META_AD_AOV",
    defaultMessage: "The average value of orders made through this platform",
  },
  [MetricTypes.META_AD_CONVERSION_RATE]: {
    id: "Metric Tooltip: META_AD_CONVERSION_RATE",
    defaultMessage: "The percentage of clicks that eventually result in a conversion",
  },
  [MetricTypes.META_AD_COST_PER_CONVERSION]: {
    id: "Metric Tooltip: META_AD_COST_PER_CONVERSION",
    defaultMessage: "The average cost of a conversion",
  },
  [MetricTypes.META_AD_COST_PER_MILLE]: {
    id: "Metric Tooltip: META_AD_COST_PER_MILLE",
    defaultMessage: "The average cost of a thousand impresssions",
  },
  [MetricTypes.META_AD_COST_PER_CLICK]: {
    id: "Metric Tooltip: META_AD_COST_PER_CLICK",
    defaultMessage: "The average cost of a single click",
  },
  [MetricTypes.META_AD_ATTRIBUTED_CONVERSION_COUNT]: {
    id: "Metric Tooltip: META_AD_ATTRIBUTED_CONVERSION_COUNT",
    defaultMessage: "The count of all reported conversions",
  },
  [MetricTypes.META_AD_ATTRIBUTED_CONVERSION_VALUE]: {
    id: "Metric Tooltip: META_AD_ATTRIBUTED_CONVERSION_VALUE",
    defaultMessage: "Total revenue attributed to this segment",
  },
  [MetricTypes.META_AD_ATTRIBUTED_CONVERSION_ROAS]: {
    id: "Metric Tooltip: META_AD_ATTRIBUTED_CONVERSION_ROAS",
    defaultMessage: "The ratio of amount spent on this adset to the reported conversion value",
  },
  [MetricTypes.GOOGLE_AD_CLICKS]: { id: "Metric Tooltip: GOOGLE_AD_CLICKS", defaultMessage: "All clicks on your ad" },
  [MetricTypes.GOOGLE_AD_FREQUENCY]: {
    id: "Metric Tooltip: GOOGLE_AD_FREQUENCY",
    defaultMessage: "Average number of times a unique individual sees your ad (Impressions/Reach)",
  },
  [MetricTypes.GOOGLE_AD_IMPRESSIONS]: {
    id: "Metric Tooltip: GOOGLE_AD_IMPRESSIONS",
    defaultMessage: "How many times your ad was displayed on a screen",
  },
  [MetricTypes.GOOGLE_AD_REACH]: {
    id: "Metric Tooltip: GOOGLE_AD_REACH",
    defaultMessage: "Number of unique individuals who saw your ad",
  },
  [MetricTypes.GOOGLE_AD_SPEND]: {
    id: "Metric Tooltip: GOOGLE_AD_SPEND",
    defaultMessage: "Google Ads marketing spend based on Google platform",
  },
  [MetricTypes.GOOGLE_AD_CTR]: {
    id: "Metric Tooltip: GOOGLE_AD_CTR",
    defaultMessage: "The ratio of sessions that result in clicks",
  },
  [MetricTypes.GOOGLE_AD_AOV]: {
    id: "Metric Tooltip: GOOGLE_AD_AOV",
    defaultMessage: "The average value of orders made through this platform",
  },
  [MetricTypes.GOOGLE_AD_CONVERSION_RATE]: {
    id: "Metric Tooltip: GOOGLE_AD_CONVERSION_RATE",
    defaultMessage: "Number of purchases attributed to this ad campaign",
  },
  [MetricTypes.GOOGLE_AD_COST_PER_CONVERSION]: {
    id: "Metric Tooltip: GOOGLE_AD_COST_PER_CONVERSION",
    defaultMessage: "Average amount spent to generate a purchase attributed to your ad (Spend/Purchases)",
  },
  [MetricTypes.GOOGLE_AD_ATTRIBUTED_CONVERSION_COUNT]: {
    id: "Metric Tooltip: GOOGLE_AD_ATTRIBUTED_CONVERSION_COUNT",
    defaultMessage: "Number of purchases attributed to this segment",
  },
  [MetricTypes.GOOGLE_AD_ATTRIBUTED_CONVERSION_VALUE]: {
    id: "Metric Tooltip: GOOGLE_AD_ATTRIBUTED_CONVERSION_VALUE",
    defaultMessage: "Total revenue attributed to this segment",
  },
  [MetricTypes.GOOGLE_AD_ATTRIBUTED_CONVERSION_ROAS]: {
    id: "Metric Tooltip: GOOGLE_AD_ATTRIBUTED_CONVERSION_ROAS",
    defaultMessage: "Total revenue attributed to your ad divided by total amount spent on ad (Revenue/Spend)",
  },
  [MetricTypes.TIKTOK_AD_SPEND]: {
    id: "Metric Tooltip: TIKTOK_AD_SPEND",
    defaultMessage: "Amount spent on TikTok Ad campaigns",
  },
  [MetricTypes.TIKTOK_ACCOUNT_ID]: {
    id: "Metric Tooltip: TIKTOK_ACCOUNT_ID",
    defaultMessage: "The ID of the TikTok Account",
  },
  [MetricTypes.TIKTOK_ACCOUNT_NAME]: {
    id: "Metric Tooltip: TIKTOK_ACCOUNT_NAME",
    defaultMessage: "The name of the TikTok Account",
  },
  [MetricTypes.TIKTOK_WAYFLYER_BC_ID]: {
    id: "Metric Tooltip: TIKTOK_WAYFLYER_BC_ID",
    defaultMessage: "The ID of the TikTok x Wayflyer Business Center",
  },
  [MetricTypes.GROSS_ITEM_QUANTITY]: {
    id: "Metric Tooltip: GROSS_ITEM_QUANTITY",
    defaultMessage: "The total number of units ordered",
  },
  [MetricTypes.GROSS_ITEM_QUANTITY_LESS_REFUNDED_QUANTITY_FROM_PRODUCTS]: {
    id: "Metric Tooltip: GROSS_ITEM_QUANTITY_LESS_REFUNDED_QUANTITY_FROM_PRODUCTS",
    defaultMessage: "The total number of units ordered less the number of units returned",
  },
  [MetricTypes.GROSS_ITEM_QUANTITY_LESS_REFUNDED_QUANTITY_FROM_REFUNDS]: {
    id: "Metric Tooltip: GROSS_ITEM_QUANTITY_LESS_REFUNDED_QUANTITY_FROM_REFUNDS",
    defaultMessage: "The total number of units ordered less the number of units returned",
  },
  [MetricTypes.GROSS_PRODUCT_REVENUE_FROM_ORDER_PROPORTION_LESS_DISCOUNTS]: {
    id: "Metric Tooltip: GROSS_PRODUCT_REVENUE_FROM_ORDER_PROPORTION_LESS_DISCOUNTS",
    defaultMessage: "Gross sales minus discounts plus taxes and shipping",
  },
  [MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_LESS_REFUNDS_FROM_ORDERS_PLUS_SHIPPING_PLUS_TAX]: {
    id: "Metric Tooltip: GROSS_REVENUE_LESS_DISCOUNTS_LESS_REFUNDS_FROM_ORDERS_PLUS_SHIPPING_PLUS_TAX",
    defaultMessage: "Gross sales minus discounts and returns plus taxes and shipping",
  },
  [MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_PLUS_SHIPPING_PLUS_TAX]: {
    id: "Metric Tooltip: GROSS_REVENUE_LESS_DISCOUNTS_PLUS_SHIPPING_PLUS_TAX",
    defaultMessage: "Gross Sales, less discounts, plus shipping and tax",
  },
  [MetricTypes.ITEM_LEVEL_NEW_CUSTOMER_PERCENT]: {
    id: "Metric Tooltip: ITEM_LEVEL_NEW_CUSTOMER_PERCENT",
    defaultMessage: "The percentage of orders placed by new customers",
  },
  [MetricTypes.ITEM_LEVEL_NEW_CUSTOMER_PERCENT_FROM_PRODUCTS]: {
    id: "Metric Tooltip: ITEM_LEVEL_NEW_CUSTOMER_PERCENT_FROM_PRODUCTS",
    defaultMessage: "The percentage of orders placed by new customers",
  },
  [MetricTypes.ITEM_LEVEL_RETURN_RATE_FROM_PRODUCTS]: {
    id: "Metric Tooltip: ITEM_LEVEL_RETURN_RATE_FROM_PRODUCTS",
    defaultMessage: "The percentage of the total units sold that have subsequently been returned",
  },
  [MetricTypes.ITEM_LEVEL_RETURN_RATE_FROM_REFUNDS]: {
    id: "Metric Tooltip: ITEM_LEVEL_RETURN_RATE_FROM_REFUNDS",
    defaultMessage: "The percentage of the total units sold that have subsequently been returned",
  },
  [MetricTypes.RETURNING_CUSTOMER_PERCENTAGE]: {
    id: "Metric Tooltip: RETURNING_CUSTOMER_PERCENTAGE",
    defaultMessage: "The percentage of orders placed by returning customers",
  },
  [MetricTypes.PRODUCT_REFUNDS_FROM_PRODUCTS]: {
    id: "Metric Tooltip: PRODUCT_REFUNDS_FROM_PRODUCTS",
    defaultMessage: "The total number of units returned",
  },
  [MetricTypes.PRODUCT_REFUNDS_FROM_REFUNDS]: {
    id: "Metric Tooltip: PRODUCT_REFUNDS_FROM_REFUNDS",
    defaultMessage: "The total number of units returned",
  },
  [MetricTypes.ROAS]: { id: "Metric Tooltip: ROAS", defaultMessage: "Total sales divided by marketing spend" },
  [MetricTypes.TOTAL_ORDERED_ITEM_QUANTITY]: {
    id: "Metric Tooltip: TOTAL_ORDERED_ITEM_QUANTITY",
    defaultMessage: "Total number of individual units sold",
  },
  [MetricTypes.TOTAL_ORDERED_ITEM_QUANTITY_LESS_REFUNDED_ITEM_QUANTITY]: {
    id: "Metric Tooltip: TOTAL_ORDERED_ITEM_QUANTITY_LESS_REFUNDED_ITEM_QUANTITY",
    defaultMessage: "Total number of items ordered less the number of items refunded",
  },
  [MetricTypes.TOTAL_ORDERED_ITEM_QUANTITY_LESS_REFUNDED_ITEM_QUANTITY_BY_REFUNDED_DATE]: {
    id: "Metric Tooltip: TOTAL_ORDERED_ITEM_QUANTITY_LESS_REFUNDED_ITEM_QUANTITY_BY_REFUNDED_DATE",
    defaultMessage: "Total number of items ordered less the number of items refunded",
  },
  [MetricTypes.TOTAL_ORDER_QUANTITY]: {
    id: "Metric Tooltip: TOTAL_ORDER_QUANTITY",
    defaultMessage: "Total number of orders placed",
  },
  [MetricTypes.TOTAL_UNIQUE_NEW_CUSTOMER_COUNT]: {
    id: "Metric Tooltip: TOTAL_UNIQUE_NEW_CUSTOMER_COUNT",
    defaultMessage: "Total number of new customers",
  },
  [MetricTypes.PROFIT]: { id: "Metric Tooltip: PROFIT", defaultMessage: "Net Profit" },
  [MetricTypes.PERCENTAGE_OF_TOTAL_AD_SPEND]: {
    id: "Metric Tooltip: PERCENTAGE_OF_TOTAL_AD_SPEND",
    defaultMessage: "Percentage of total spend for this segment",
  },
  [MetricTypes.AVERAGE_UNITS_PER_ORDER]: {
    id: "Metric Tooltip: AVERAGE_UNITS_PER_ORDER",
    defaultMessage: "Average number of units sold per order",
  },
  [MetricTypes.REFUNDED_ITEM_PERCENTAGE]: {
    id: "Metric Tooltip: REFUNDED_ITEM_PERCENTAGE",
    defaultMessage: "Percentage of total units that have been refunded",
  },
  [MetricTypes.REFUNDED_ITEM_PERCENTAGE_BY_REFUNDED_DATE]: {
    id: "Metric Tooltip: REFUNDED_ITEM_PERCENTAGE_BY_REFUNDED_DATE",
    defaultMessage: "Percentage of total units that have been refunded",
  },
  [MetricTypes.AVERAGE_DAILY_UNITS]: {
    id: "Metric Tooltip: AVERAGE_DAILY_UNITS",
    defaultMessage: "Average number of units sold per day",
  },
  [MetricTypes.SALES_VELOCITY_THIRTY_DAYS]: {
    id: "Metric Tooltip: SALES_VELOCITY_THIRTY_DAYS",
    defaultMessage: "The number of units sold per day in the past 30 days",
  },
  [MetricTypes.PRODUCT_LEVEL_DISCOUNT_PCT]: {
    id: "Metric Tooltip: PRODUCT_LEVEL_DISCOUNT_PCT",
    defaultMessage: "Average % discount applied to product (excluding whole order discounts)",
  },
  [MetricTypes.SESSION_COUNT]: {
    id: "Metric Tooltip: SESSION_COUNT",
    defaultMessage: "Number of sessions on your online store within a given timeframe",
  },
  [MetricTypes.CONVERSION_RATE]: {
    id: "Metric Tooltip: CONVERSION_RATE",
    defaultMessage: "Percentage of sessions that resulted in orders, out of the total number of sessions",
  },
  [MetricTypes.STORE_NAME]: { id: "Metric Tooltip: STORE_NAME", defaultMessage: "The store that sells this product" },
  [MetricTypes.ADSET_FIRST_USED]: {
    id: "Metric Tooltip: ADSET_FIRST_USED",
    defaultMessage: "The date the adset was first used",
  },
  [MetricTypes.ADSET_LAST_USED]: {
    id: "Metric Tooltip: ADSET_LAST_USED",
    defaultMessage: "The date the adset was most recently used",
  },
  [MetricTypes.ADSET_REGION]: {
    id: "Metric Tooltip: ADSET_REGION",
    defaultMessage: "The region targeted by the adset",
  },
  [MetricTypes.ADSET_NAME]: { id: "Metric Tooltip: ADSET_NAME", defaultMessage: "The name of the adset" },
  [MetricTypes.ADSET_AUDIENCE]: {
    id: "Metric Tooltip: ADSET_AUDIENCE",
    defaultMessage: "The classification of the adset's audience targeting",
  },
  [MetricTypes.CAMPAIGN_OBJECTIVE]: {
    id: "Metric Tooltip: CAMPAIGN_OBJECTIVE",
    defaultMessage: "The objective of the parent campaign",
  },
});

/**
 * The metric's display name.
 *
 * Takes a plain string as well as a `MetricTypes`: `columnForMetric` is given column names straight from the
 * dashboard payload, and not all of them are metrics. An unknown name is returned as-is, which is what the gettext
 * catalog did for anything it had no entry for.
 */
export const metricDescription = (intl: IntlShape, metric: MetricTypes | string): string =>
  Object.hasOwn(metricDescriptions, metric) ? intl.formatMessage(metricDescriptions[metric as MetricTypes]) : metric;

/**
 * The metric's tooltip, or `undefined` where no copy has been written for it.
 *
 * Takes a plain string as well as a `MetricTypes`, because table column ids are untyped; a column that is not a
 * metric simply has no tooltip.
 */
export const metricTooltip = (intl: IntlShape, metric: MetricTypes | string): string | undefined => {
  // `hasOwn`, not a plain index: a column id such as "constructor" would otherwise resolve to an inherited
  // Object.prototype member and reach formatMessage as a bogus descriptor.
  if (!Object.hasOwn(metricTooltips, metric)) {
    return undefined;
  }
  const descriptor = metricTooltips[metric as MetricTypes];
  return descriptor === undefined ? undefined : intl.formatMessage(descriptor);
};
